/**
 * Reference Data Management Page
 * Manages all reference data tables
 */

import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormHelperText,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
  Switch,
  FormControlLabel,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Restore as RestoreIcon,
  Assessment as AssessmentIcon,
  History as HistoryIcon,
  Block as BlockIcon,
} from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';
import { apiGet, apiPost, apiPut, apiDelete } from '../utils/apiClient';
import { formatNumber, formatRatePct } from '../utils/formatters';
import { usePermissions } from '../contexts/PermissionContext';
import { useNotification } from '../contexts/NotificationContext';
import { loadImpactPromptPreference, saveImpactPromptPreference } from '../utils/uiPreferences';
import { useTenant } from '../contexts/TenantContext';
import { MODULES, PERMISSION_LEVELS } from '../contexts/PermissionConstants';
import { a11yProps } from '../components/common/TabPanel';
import ConfirmDialog from '../components/common/ConfirmDialog';
import DataTable from '../components/common/DataTable';
import useConfirmDialog from '../hooks/useConfirmDialog';
import usePagination from '../hooks/usePagination';
import useTableSort from '../hooks/useTableSort';
import logger from '../utils/logger';

// API base path per reference-data type. Single source of truth shared by the
// create/update (onSubmit), delete, and reactivate handlers.
const REFDATA_ENDPOINTS = {
  countries: '/refdata/countries',
  frequencies: '/refdata/frequencies',
  investmentTypes: '/refdata/investment-types',
  documentTypes: '/refdata/document-types',
  programs: '/refdata/programs',
  interestRates: '/refdata/interest-rates',
  fxRates: '/refdata/fx-rates',
  placementFees: '/refdata/placement-fees',
  vatRates: '/refdata/vat-rates',
  collateralTypes: '/refdata/collateral-types',
  narrations: '/refdata/narrations',
  whtRules: '/refdata/wht-rules',
  commissionRates: '/refdata/commission-rates',
};

/**
 * The two kinds of row in `refdata_placement_fees_and_duty`, keyed by the `kind` on the tab.
 *
 * ⚠ THESE `type` STRINGS ARE LOAD-BEARING AND MUST MATCH EXACTLY. The contract wizard selects its
 * options by exact string — `BrokerSection.js` filters `f.type === 'Broker Fee'`, and
 * `StampDutySection.js` filters `f.type === 'Stamp Duty'` — and the database enforces the broker
 * side too (T3-138: `sp_contract_broker_fee_upsert` rejects a reference row whose type is not
 * 'Broker Fee'), as does the stamp-duty side (`sp_contract_add_stamp_duty` /
 * `sp_contract_update_stamp_duty`).
 *
 * There is NO CHECK constraint on the column, so a mistyped value here would be accepted by the
 * database and then be selectable in NEITHER wizard section — visible only on this screen and
 * usable nowhere. That exact failure was reproduced on dev before this split: a row created with
 * `type = 'broker fee'` was stored, listed here, and absent from both wizard dropdowns.
 * Seeding the type from this map is what makes that impossible.
 */
const PLACEMENT_FEE_KINDS = {
  brokerFee: {
    type: 'Broker Fee',
    addLabel: 'Add Broker Fee',
    emptyMessage: 'No broker fee rates found',
  },
  stampDuty: {
    type: 'Stamp Duty',
    addLabel: 'Add Stamp Duty',
    emptyMessage: 'No stamp duty rates found',
  },
};

function ReferenceDataManagement() {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const { isFeatureEnabled } = useTenant();
  const notify = useNotification();
  const [tabValue, setTabValue] = useState(0);
  const [interestRateCountryTab, setInterestRateCountryTab] = useState(0);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [dataType, setDataType] = useState('countries');
  const [submitError, setSubmitError] = useState(null);
  const confirm = useConfirmDialog();

  // Per-tab "show inactive" flags, keyed by refdata type (e.g. showInactive.countries).
  // Falsy = active-only (default). When on, that type is fetched with active_only=false.
  const [showInactive, setShowInactive] = useState({});
  // Per-tab selection of inactive row ids for bulk reactivate, keyed by type.
  const [selected, setSelected] = useState({});

  const canWrite = hasPermission(MODULES.REFERENCE_DATA, PERMISSION_LEVELS.WRITE);
  const canAdmin = hasPermission(MODULES.REFERENCE_DATA, PERMISSION_LEVELS.ADMIN);

  const countriesPagination = usePagination({ defaultRowsPerPage: 10 });
  const frequenciesPagination = usePagination({ defaultRowsPerPage: 10 });
  const investmentTypesPagination = usePagination({ defaultRowsPerPage: 10 });
  const documentTypesPagination = usePagination({ defaultRowsPerPage: 10 });
  const programsPagination = usePagination({ defaultRowsPerPage: 10 });
  const fxRatesPagination = usePagination({ defaultRowsPerPage: 10 });
  const placementFeesPagination = usePagination({ defaultRowsPerPage: 10 });
  const vatRatesPagination = usePagination({ defaultRowsPerPage: 10 });
  const collateralTypesPagination = usePagination({ defaultRowsPerPage: 10 });
  const narrationsPagination = usePagination({ defaultRowsPerPage: 10 });
  const whtRulesPagination = usePagination({ defaultRowsPerPage: 10 });
  const commissionRatesPagination = usePagination({ defaultRowsPerPage: 10 });

  const countriesSort = useTableSort({ onSortChange: countriesPagination.resetPage });
  const frequenciesSort = useTableSort({ onSortChange: frequenciesPagination.resetPage });
  const investmentTypesSort = useTableSort({ onSortChange: investmentTypesPagination.resetPage });
  const documentTypesSort = useTableSort({ onSortChange: documentTypesPagination.resetPage });
  const programsSort = useTableSort({ onSortChange: programsPagination.resetPage });
  const fxRatesSort = useTableSort({ onSortChange: fxRatesPagination.resetPage });
  const placementFeesSort = useTableSort({ onSortChange: placementFeesPagination.resetPage });
  const vatRatesSort = useTableSort({ onSortChange: vatRatesPagination.resetPage });
  const collateralTypesSort = useTableSort({ onSortChange: collateralTypesPagination.resetPage });
  const narrationsSort = useTableSort({ onSortChange: narrationsPagination.resetPage });
  const whtRulesSort = useTableSort({ onSortChange: whtRulesPagination.resetPage });
  const commissionRatesSort = useTableSort({ onSortChange: commissionRatesPagination.resetPage });

  // Impact assessment dialog state
  const [impactDialog, setImpactDialog] = useState({
    open: false,
    rateType: '',
    countryId: '',
    newRate: '',
    effectiveDate: '',
    benchmarkRateId: '',
  });

  // Whether saving an interest rate prompts to run an impact assessment. Persisted per user so a
  // bulk keying session isn't interrupted on every save; the header "Impact Assessment" button
  // still runs it on demand afterwards.
  const [promptImpact, setPromptImpact] = useState(loadImpactPromptPreference);

  // When the add dialog is opened from a specific rate group, the group's (country, type) is seeded
  // into the form and shown as context text instead of two editable pickers. Holding it in state is
  // what lets the form render read-only - the values themselves live in react-hook-form.
  const [dialogSeed, setDialogSeed] = useState(null);

  // Batch historic entry ("Add history") dialog. Rows are plain state, mirroring
  // AdhocInterestPaymentSection; rowKeySeq gives stable keys (Date.now() collides within a ms).
  const [batchDialog, setBatchDialog] = useState(null);
  const [batchRows, setBatchRows] = useState([]);
  const [batchSubmitting, setBatchSubmitting] = useState(false);
  const [batchError, setBatchError] = useState(null);
  const [batchFailedRow, setBatchFailedRow] = useState(null);
  const rowKeySeq = useRef(0);

  // Data states
  const [countries, setCountries] = useState([]);
  const [frequencies, setFrequencies] = useState([]);
  const [investmentTypes, setInvestmentTypes] = useState([]);
  const [documentTypes, setDocumentTypes] = useState([]);
  // Per-product document requirements at submit-for-approval, for the investment
  // type currently open in the add/edit dialog. Dynamic: one entry per candidate
  // submission document type, shape { document_type_code, document_type_name,
  // per_investor, is_required }. Rebuilt from the product's saved requirements +
  // the active submission-document types when the dialog opens (T3-039).
  const [docReqs, setDocReqs] = useState([]);
  // The product's saved requirement rows (from GET), and whether this is a brand-new
  // product (defaults every candidate to required). `docReqs` is derived from these
  // + the loaded documentTypes in an effect, so it is rebuilt once documentTypes
  // finishes loading (avoids the async race where a partial candidate list dropped
  // rows from the save payload). `null` saved = not an investmentTypes dialog / not loaded.
  const [productDocReqSaved, setProductDocReqSaved] = useState(null);
  const [productDocReqDefaultRequired, setProductDocReqDefaultRequired] = useState(false);
  const [programs, setPrograms] = useState([]);
  const [issuersList, setIssuersList] = useState([]);
  const [interestRates, setInterestRates] = useState([]);
  const [fxRates, setFxRates] = useState([]);
  const [placementFees, setPlacementFees] = useState([]);
  const [vatRates, setVatRates] = useState([]);
  const [collateralTypes, setCollateralTypes] = useState([]);
  const [narrations, setNarrations] = useState([]);
  const [whtRules, setWhtRules] = useState([]);
  const [commissionRates, setCommissionRates] = useState([]);
  const [brokersList, setBrokersList] = useState([]);
  const [investorsList, setInvestorsList] = useState([]);

  const { control, handleSubmit, reset, setValue, watch } = useForm();

  // Watch form fields for Interest Rates form inter-field dependencies
  const watchedCountryId = watch('country_id');
  const watchedInterestRateType = watch('interest_rate_type');

  useEffect(() => {
    loadAllData();
  }, []);

  // Reset country sub-tab if selected index goes out of bounds after data reload
  useEffect(() => {
    const uniqueCountryIds = new Set(interestRates.map(r => r.country_id).filter(Boolean));
    if (interestRateCountryTab > uniqueCountryIds.size) {
      setInterestRateCountryTab(0);
    }
  }, [interestRates, interestRateCountryTab]);

  // Auto-populate Prime Rate Institution when country and rate type are selected
  useEffect(() => {
    if (dataType === 'interestRates' && watchedCountryId && watchedInterestRateType) {
      // Find the most recent rate with this country and type to get the institution
      const matchingRates = interestRates
        .filter(r => r.country_id === watchedCountryId && r.interest_rate_type === watchedInterestRateType)
        .sort((a, b) => new Date(b.effective_date) - new Date(a.effective_date));

      if (matchingRates.length > 0 && matchingRates[0].prime_rate_institution) {
        setValue('prime_rate_institution', matchingRates[0].prime_rate_institution);
      }
    }
  }, [dataType, watchedCountryId, watchedInterestRateType, interestRates, setValue]);

  // Maps each refdata type to its API path + state setter, so a single type can be
  // (re)fetched independently with its own active_only flag. The state setters are
  // stable across renders, so this object is built once.
  const refdataSetters = useMemo(() => ({
    countries: setCountries,
    frequencies: setFrequencies,
    investmentTypes: setInvestmentTypes,
    documentTypes: setDocumentTypes,
    programs: setPrograms,
    interestRates: setInterestRates,
    fxRates: setFxRates,
    placementFees: setPlacementFees,
    vatRates: setVatRates,
    collateralTypes: setCollateralTypes,
    narrations: setNarrations,
    whtRules: setWhtRules,
    commissionRates: setCommissionRates,
  }), []);

  // Fetch a single refdata type. When showInactiveFlag is true, includes inactive
  // rows (active_only=false); otherwise active-only (the default backend behaviour).
  const loadRefdataType = async (type, showInactiveFlag) => {
    const setter = refdataSetters[type];
    if (!setter) return;
    try {
      const res = await apiGet(`${REFDATA_ENDPOINTS[type]}?active_only=${!showInactiveFlag}`);
      let rows = res?.data || [];
      // Document types use is_active; mirror it to `active` so the generic table
      // chip / inactive-ordering / reactivate machinery works unchanged (T3-039).
      if (type === 'documentTypes') {
        rows = rows.map((r) => ({ ...r, active: r.is_active }));
      }
      setter(rows);
    } catch (err) {
      logger.error('Error loading reference data', { type, message: err.message, status: err.response?.status });
      setter([]);
    }
  };

  // Reload one type using its current per-tab show-inactive flag.
  const reloadType = (type) => loadRefdataType(type, !!showInactive[type]);

  const loadAllData = async () => {
    // Each refdata type respects its own show-inactive flag; on first mount all
    // flags are falsy, so this loads active-only across the board (unchanged).
    const refdataLoads = Object.keys(refdataSetters).map((type) =>
      loadRefdataType(type, !!showInactive[type])
    );

    // Supporting stakeholder lists for the program/commission forms (no active_only param).
    const stakeholderLoads = (async () => {
      try {
        // Fetch the full lists (client-filtered autocompletes below); the list
        // routes default to limit=25, so omit no limit or stakeholders past #25
        // become unselectable in the program/commission forms.
        const [brokersListData, investorsListData, issuersData] = await Promise.all([
          apiGet('/stakeholders/brokers?page=1&limit=100000').catch(() => []),
          apiGet('/stakeholders/investors?page=1&limit=100000').catch(() => []),
          apiGet('/stakeholders/issuers?page=1&limit=100000').catch(() => []),
        ]);
        setBrokersList(brokersListData?.data || []);
        setInvestorsList(investorsListData?.data || []);
        setIssuersList(issuersData?.data || []);
      } catch (err) {
        logger.error('Error loading stakeholder lists', { message: err.message, status: err.response?.status });
      }
    })();

    await Promise.all([...refdataLoads, stakeholderLoads]);
  };

  // Helper to format date for input[type="date"] (requires YYYY-MM-DD)
  const formatDateForInput = (dateValue) => {
    if (!dateValue) return '';
    const date = new Date(dateValue);
    if (isNaN(date.getTime())) return '';
    return date.toISOString().split('T')[0];
  };

  // `seed` pre-fills fields when adding from a known context (e.g. the Add button on a specific
  // interest-rate group card), so the user doesn't re-pick what is already on screen. Safe to
  // setValue straight after reset(): useForm() has no defaultValues, so reset() clears to {}, and
  // RHF's useController reads _formValues before its own defaultValue prop - so a seeded value wins
  // on mount rather than being clobbered by `defaultValue=""`.
  const handleOpenDialog = (type, item = null, seed = null) => {
    setDataType(type);
    setEditingItem(item);
    setDialogSeed(seed);
    if (item) {
      Object.keys(item).forEach(key => {
        // Format date fields for input[type="date"]
        if (key === 'effective_date' || key.endsWith('_date') || key === 'effective_from' || key === 'effective_to') {
          setValue(key, formatDateForInput(item[key]));
        } else {
          setValue(key, item[key]);
        }
      });
      // Split currency_pair into from_currency and to_currency for FX rates
      if (type === 'fxRates' && item.currency_pair) {
        const [from, to] = item.currency_pair.split('/');
        setValue('from_currency', from || '');
        setValue('to_currency', to || '');
      }
      // Map the program's investment_types array of objects to an ids array
      if (type === 'programs') {
        const ids = Array.isArray(item.investment_types) ? item.investment_types.map((t) => t.id) : [];
        setValue('investment_type_ids', ids);
      }
      // Load the product's current submit-for-approval document requirements so the
      // switches reflect what is configured. Default to required if a row is absent.
      if (type === 'investmentTypes') {
        // Load this product's saved requirements into state; `docReqs` is derived
        // from these + documentTypes by an effect (so it rebuilds when documentTypes
        // finishes loading — no partial-list race). Editing an existing product:
        // absent candidate rows default to NOT required.
        setProductDocReqDefaultRequired(false);
        setProductDocReqSaved([]);
        apiGet(`/refdata/investment-types/${item.id}/document-requirements`)
          .then((res) => {
            setProductDocReqSaved(Array.isArray(res?.data) ? res.data : []);
          })
          .catch((err) => {
            logger.warn('Failed to load product document requirements', { message: err.message });
            setProductDocReqSaved([]);
          });
      }
    } else {
      reset();
      if (seed) {
        Object.entries(seed).forEach(([key, value]) => setValue(key, value));
      }
      // New product: every candidate submission-document type defaults to required
      // (server trigger seeds the same defaults; the switches let the admin opt out
      // before first save).
      if (type === 'investmentTypes') {
        setProductDocReqDefaultRequired(true);
        setProductDocReqSaved([]);
      }
    }
    setSubmitError(null);
    setDialogOpen(true);
  };

  // Build the per-product document-requirement switch rows. The candidate set is
  // the active submission-document types UNION any type present in the product's
  // saved rows (so a saved requirement is always represented — and therefore
  // re-sent on save — even if its type is missing/deactivated from documentTypes).
  // This is the fix for the async race that previously dropped rows from the PUT.
  // `defaultRequired` = fallback for a candidate with no saved row (new product).
  const buildDocReqRows = (savedRows, defaultRequired = false) => {
    const saved = Array.isArray(savedRows) ? savedRows : [];
    const candidates = (documentTypes || []).filter((dt) => dt.is_submission_document && dt.is_active);
    const byCode = new Map();
    for (const dt of candidates) {
      byCode.set(dt.code, {
        document_type_code: dt.code,
        document_type_name: dt.display_name,
        per_investor: dt.per_investor === true,
        sort_order: dt.sort_order ?? 0,
      });
    }
    // Union in any saved row whose type isn't already a candidate.
    for (const r of saved) {
      if (!byCode.has(r.document_type_code)) {
        byCode.set(r.document_type_code, {
          document_type_code: r.document_type_code,
          document_type_name: r.document_type_name || r.document_type_code,
          per_investor: r.per_investor === true,
          sort_order: r.sort_order ?? 0,
        });
      }
    }
    return Array.from(byCode.values())
      .sort((a, b) => (a.sort_order - b.sort_order) || a.document_type_code.localeCompare(b.document_type_code))
      .map((c) => {
        const row = saved.find((r) => r.document_type_code === c.document_type_code);
        return {
          document_type_code: c.document_type_code,
          document_type_name: c.document_type_name,
          per_investor: c.per_investor,
          is_required: row ? row.is_required === true : defaultRequired,
        };
      });
  };

  // Derive docReqs from the loaded documentTypes + the product's saved rows.
  // Re-runs when documentTypes finishes loading (so the switch list is complete
  // regardless of load order) and when a different product dialog opens.
  useEffect(() => {
    if (dialogOpen && dataType === 'investmentTypes' && productDocReqSaved !== null) {
      setDocReqs(buildDocReqRows(productDocReqSaved, productDocReqDefaultRequired));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dialogOpen, dataType, documentTypes, productDocReqSaved, productDocReqDefaultRequired]);

  const setDocReqRequired = (code, required) => {
    setDocReqs((prev) => prev.map((r) => (r.document_type_code === code ? { ...r, is_required: required } : r)));
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingItem(null);
    setSubmitError(null);
    setDialogSeed(null);
    // Clear per-product doc-req state so it doesn't leak into the next dialog.
    setProductDocReqSaved(null);
    setDocReqs([]);
    reset();
  };

  const onSubmit = async (data) => {
    // T3-065: mirror the SP's base-required GL guard for immediate feedback. The External
    // (base) pair is the mandatory fallback the Related pair defers to, so a Related-only /
    // base-blank config is rejected (both-blank stays allowed). The server enforces this
    // authoritatively; this only saves a round-trip.
    if (dataType === 'investmentTypes') {
      const trimmed = (v) => (v ?? '').toString().trim();
      const baseSet = trimmed(data.cr_control_account) || trimmed(data.dr_expense_account);
      const relatedSet = trimmed(data.related_cr_control_account) || trimmed(data.related_dr_expense_account);
      if ((baseSet || relatedSet) && !baseSet) {
        setSubmitError('The External (base) Control and Expense accounts are required when GL accounts are set; the Related pair is an optional override.');
        return;
      }
    }
    try {
      const endpoint = REFDATA_ENDPOINTS[dataType];
      let savedId = null;

      // Combine from_currency and to_currency into currency_pair for FX rates
      let submitData = { ...data };
      if (dataType === 'fxRates' && data.from_currency && data.to_currency) {
        submitData.currency_pair = `${data.from_currency}/${data.to_currency}`;
        delete submitData.from_currency;
        delete submitData.to_currency;
      }

      if (editingItem) {
        await apiPut(`${endpoint}/${editingItem.id}`, submitData);
        savedId = editingItem.id;
      } else {
        const response = await apiPost(endpoint, submitData);
        savedId = response?.data?.id;
      }

      // Persist per-product submit-for-approval document requirements. One row per
      // candidate submission-document type; per_investor is derived server-side from
      // the document type. Upsert is idempotent, so this also covers a just-created
      // product (the server trigger already seeded its defaults).
      // Guard: only PUT when docReqs actually reflects the candidate set. If the
      // document types hadn't loaded yet (empty docReqs), skip rather than send an
      // empty array that would touch nothing / mislead — the derive effect rebuilds
      // it once loaded, so a real save always has rows.
      if (dataType === 'investmentTypes' && savedId && docReqs.length > 0) {
        await apiPut(`/refdata/investment-types/${savedId}/document-requirements`, {
          requirements: docReqs.map((r) => ({
            document_type_code: r.document_type_code,
            is_required: r.is_required === true,
            per_investor: r.per_investor === true,
          })),
        });
      }

      await loadAllData();
      handleCloseDialog();

      // Show impact assessment prompt for interest rates. Suppressed when the user has toggled the
      // prompt off (keying a batch of rates), in which case the save is confirmed with a toast and
      // the assessment is run once afterwards via the header "Impact Assessment" button.
      if (dataType === 'interestRates' && data.interest_rate_type && data.country_id) {
        if (promptImpact) {
          setImpactDialog({
            open: true,
            rateType: data.interest_rate_type,
            countryId: data.country_id,
            newRate: data.effective_interest_rate,
            effectiveDate: data.effective_date,
            benchmarkRateId: savedId || '',
          });
        } else {
          notify.success(`Interest rate saved for ${data.interest_rate_type}`);
        }
      }
    } catch (err) {
      logger.error('Error saving reference data', { message: err.message, status: err.response?.status });
      const apiMessage = err.response?.data?.error?.message;
      setSubmitError(apiMessage || err.message || 'Failed to save reference data');
    }
  };

  // ---------------------------------------------------------------------------
  // Batch historic interest-rate entry ("Add history")
  //
  // Loads a whole historic series for ONE (country, rate type) group in a single all-or-nothing
  // request. Rows are plain state (this codebase does not use useFieldArray anywhere) and follow the
  // AdhocInterestPaymentSection shape: add / update-by-index / remove-by-index.
  // ---------------------------------------------------------------------------

  const makeBatchRow = () => ({ key: ++rowKeySeq.current, effective_date: '', effective_interest_rate: '' });

  const handleOpenBatchDialog = (group) => {
    setBatchDialog(group);
    setBatchRows([makeBatchRow(), makeBatchRow(), makeBatchRow()]);
    setBatchError(null);
    setBatchFailedRow(null);
  };

  const handleCloseBatchDialog = () => {
    setBatchDialog(null);
    setBatchRows([]);
    setBatchError(null);
    setBatchFailedRow(null);
    setBatchSubmitting(false);
  };

  const handleAddBatchRow = () => setBatchRows((prev) => [...prev, makeBatchRow()]);

  const handleUpdateBatchRow = (index, field, value) => {
    setBatchRows((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
    // A row-specific server error no longer applies once the row is edited.
    setBatchFailedRow((prev) => (prev === index ? null : prev));
  };

  const handleRemoveBatchRow = (index) => {
    setBatchRows((prev) => prev.filter((_, i) => i !== index));
    setBatchFailedRow(null);
  };

  // Per-row client validation, mirroring the RolloverSourcesSection pattern: derive an error string
  // per row plus one section-level message, rather than a toast that loses row context.
  const batchValidation = useMemo(() => {
    const rowErrors = batchRows.map(() => null);
    const filled = batchRows.filter((r) => r.effective_date || r.effective_interest_rate !== '');

    const seenDates = new Map();
    batchRows.forEach((row, i) => {
      const hasAny = row.effective_date || row.effective_interest_rate !== '';
      if (!hasAny) return;

      if (!row.effective_date) {
        rowErrors[i] = 'Effective date is required';
        return;
      }
      if (row.effective_interest_rate === '' || row.effective_interest_rate === null) {
        rowErrors[i] = 'Rate is required';
        return;
      }
      const rate = parseFloat(row.effective_interest_rate);
      if (isNaN(rate) || rate < 0) {
        rowErrors[i] = 'Rate must be zero or greater';
        return;
      }
      if (seenDates.has(row.effective_date)) {
        rowErrors[i] = `Duplicate date (also row ${seenDates.get(row.effective_date) + 1})`;
        return;
      }
      seenDates.set(row.effective_date, i);

      // Warn (not block) when the date already exists in the group - the server rejects it, but
      // flagging it here saves a round trip.
      if (batchDialog && interestRates.some((r) =>
        r.country_id === batchDialog.country_id &&
        r.interest_rate_type === batchDialog.interest_rate_type &&
        formatDateForInput(r.effective_date) === row.effective_date
      )) {
        rowErrors[i] = 'A rate already exists for this date in this group';
      }
    });

    const hasErrors = rowErrors.some(Boolean);
    return {
      rowErrors,
      filledCount: filled.length,
      canSubmit: filled.length > 0 && !hasErrors,
      sectionError: filled.length === 0 ? 'Add at least one rate row' : null,
    };
  }, [batchRows, batchDialog, interestRates]);

  const handleSubmitBatch = async () => {
    if (!batchDialog || !batchValidation.canSubmit) return;

    // Only rows the user actually filled in are sent; blank trailing rows are ignored.
    const rows = batchRows
      .filter((r) => r.effective_date && r.effective_interest_rate !== '')
      .map((r) => ({
        effective_date: r.effective_date,
        effective_interest_rate: parseFloat(r.effective_interest_rate),
      }));

    setBatchSubmitting(true);
    setBatchError(null);
    setBatchFailedRow(null);
    try {
      const response = await apiPost(`${REFDATA_ENDPOINTS.interestRates}/bulk`, {
        country_id: batchDialog.country_id,
        interest_rate_type: batchDialog.interest_rate_type,
        is_variable: batchDialog.is_variable === true,
        prime_rate: batchDialog.prime_rate ?? null,
        prime_rate_institution: batchDialog.prime_rate_institution || null,
        rows,
      });
      const inserted = response?.data?.inserted ?? rows.length;
      await reloadType('interestRates');
      handleCloseBatchDialog();
      notify.success(`${inserted} historic rate${inserted === 1 ? '' : 's'} added to ${batchDialog.interest_rate_type}`);
    } catch (err) {
      logger.error('Error saving interest rate history', { message: err.message, status: err.response?.status });
      const apiError = err.response?.data?.error;
      // The API attributes a failure to a row index so the exact row can be highlighted, instead of
      // string-matching a date out of the message. Nothing was saved - the batch is one transaction.
      const rowIndex = apiError?.details?.rowIndex;
      if (typeof rowIndex === 'number') setBatchFailedRow(rowIndex);
      setBatchError(apiError?.message || err.message || 'Failed to save interest rate history');
    } finally {
      setBatchSubmitting(false);
    }
  };

  // Interest rates are HARD-deleted (T3-099) — the row is removed, not hidden — so the prompt has to
  // say so. Every other tab still soft-deletes, and keeps the generic wording. Named the rate being
  // removed because the card view shows several rows per group and the icons sit close together.
  const buildDeleteMessage = (type, row) => {
    if (type !== 'interestRates' || !row) return 'Are you sure you want to delete this item?';
    const when = row.effective_date
      ? new Date(row.effective_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
      : 'unknown date';
    return `Permanently delete the ${row.effective_interest_rate}% rate effective ${when}?\n\n`
      + 'This removes it for good — it will not appear under "Show inactive". '
      + 'If any contract or rate change uses it, the deletion is refused and you can deactivate it instead.';
  };

  const handleDelete = (type, id, row = null) => {
    confirm.show({
      title: type === 'interestRates' ? 'Delete Interest Rate' : 'Delete Item',
      message: buildDeleteMessage(type, row),
      confirmText: 'Delete',
      onConfirm: async () => {
        try {
          await apiDelete(`${REFDATA_ENDPOINTS[type]}/${id}`);
          await reloadType(type);
          notify.success('Item deleted');
        } catch (err) {
          logger.error('Error deleting reference data', { message: err.message, status: err.response?.status });
          const serverMessage = err.response?.data?.error?.message || err.response?.data?.message || err.message;

          // Server guards refuse deleting a record something is still tied to and tell the user to
          // deactivate instead (e.g. T3-095 for interest rates). Offer that as an EXPLICIT second
          // decision rather than silently substituting one action for another — the user asked to
          // delete, so quietly deactivating would be a different act than the one they confirmed.
          // Only offered when we have the row to send (deactivate is a PUT of the full row) and the
          // server actually pointed at deactivation.
          if (row && canAdmin && row.active !== false && /deactivate/i.test(serverMessage)) {
            confirm.show({
              title: 'Deactivate instead?',
              message: `${serverMessage}\n\nDeactivate it now? It stays on record, anything already using it is unaffected, and you can reactivate it later.`,
              confirmText: 'Deactivate',
              cancelText: 'Leave unchanged',
              confirmColor: 'warning',
              onConfirm: async () => {
                try {
                  await apiPut(`${REFDATA_ENDPOINTS[type]}/${row.id}`, buildSetActiveBody(type, row, false));
                  await reloadType(type);
                  notify.success('Item deactivated');
                } catch (e2) {
                  logger.error('Error deactivating after refused delete', { message: e2.message, status: e2.response?.status });
                  notify.error('Failed to deactivate item: ' + (e2.response?.data?.error?.message || e2.response?.data?.message || e2.message));
                }
              },
              // Declining must be acknowledged, not silent: the delete already failed, so closing with
              // no message would leave the user unsure whether anything happened.
              onCancel: () => notify.info('No changes made — the item was neither deleted nor deactivated'),
            });
            return;
          }

          notify.error('Failed to delete item: ' + serverMessage);
        }
      },
    });
  };

  // Build the PUT body that sets a row's active flag. Reuses the existing update
  // endpoint. Most types accept the row spread directly; programs need their
  // investment_type_ids array rebuilt from the row's investment_types objects, or the
  // update validation rejects it (empty type-id list -> 400).
  const buildSetActiveBody = (type, row, active) => {
    if (type === 'programs') {
      return {
        ...row,
        investment_type_ids: Array.isArray(row.investment_types)
          ? row.investment_types.map((t) => t.id)
          : [],
        active,
      };
    }
    // Document types are updated via is_active (the PUT reads is_active).
    if (type === 'documentTypes') {
      return { ...row, is_active: active };
    }
    return { ...row, active };
  };

  // Deactivate a single row (active -> false) without deleting it. Same PUT + buildSetActiveBody path
  // as handleReactivate, just the other direction. Needed because the interest-rates card view has no
  // checkbox column, so the bulk Deactivate button in the tab header never appears there.
  const handleDeactivate = (type, row) => {
    confirm.show({
      title: 'Deactivate Item',
      message: 'Deactivate this item? It stays on record and can be reactivated later, and anything already using it is unaffected.',
      confirmText: 'Deactivate',
      onConfirm: async () => {
        try {
          await apiPut(`${REFDATA_ENDPOINTS[type]}/${row.id}`, buildSetActiveBody(type, row, false));
          await reloadType(type);
          notify.success('Item deactivated');
        } catch (err) {
          logger.error('Error deactivating reference data', { message: err.message, status: err.response?.status });
          notify.error('Failed to deactivate item: ' + (err.response?.data?.error?.message || err.response?.data?.message || err.message));
        }
      },
    });
  };

  const handleReactivate = (type, row) => {
    confirm.show({
      title: 'Reactivate Item',
      message: 'Are you sure you want to reactivate this item?',
      confirmText: 'Reactivate',
      onConfirm: async () => {
        try {
          await apiPut(`${REFDATA_ENDPOINTS[type]}/${row.id}`, buildSetActiveBody(type, row, true));
          await reloadType(type);
          notify.success('Item reactivated');
        } catch (err) {
          logger.error('Error reactivating reference data', { message: err.message, status: err.response?.status });
          notify.error('Failed to reactivate item: ' + (err.response?.data?.error?.message || err.response?.data?.message || err.message));
        }
      },
    });
  };

  // Bulk activate (active=true) or deactivate (active=false) the selected rows of a tab.
  // Reuses the existing update endpoint per row; only operates on rows whose state
  // would actually change, so selecting a mix is safe.
  const handleBulkSetActive = (type, rows, active) => {
    const ids = selected[type] || [];
    const targets = (rows || []).filter((r) => ids.includes(r.id) && r.active !== active);
    if (targets.length === 0) {
      notify.info(`No selected items to ${active ? 'activate' : 'deactivate'}`);
      return;
    }

    const verb = active ? 'activate' : 'deactivate';
    confirm.show({
      title: active ? 'Activate Items' : 'Deactivate Items',
      message: `Are you sure you want to ${verb} ${targets.length} item${targets.length === 1 ? '' : 's'}?`,
      confirmText: active ? 'Activate' : 'Deactivate',
      onConfirm: async () => {
        let failures = 0;
        let firstError = null;
        for (const row of targets) {
          try {
            await apiPut(`${REFDATA_ENDPOINTS[type]}/${row.id}`, buildSetActiveBody(type, row, active));
          } catch (err) {
            failures += 1;
            if (!firstError) firstError = err.response?.data?.error?.message || err.response?.data?.message || err.message;
            logger.error(`Error ${verb} reference data (bulk)`, { id: row.id, message: err.message, status: err.response?.status });
          }
        }
        setSelected((prev) => ({ ...prev, [type]: [] }));
        await reloadType(type);
        const succeeded = targets.length - failures;
        if (failures > 0) {
          notify.warning(`${succeeded} item${succeeded === 1 ? '' : 's'} ${verb}d, ${failures} failed${firstError ? `: ${firstError}` : ''}`);
        } else {
          notify.success(`${succeeded} item${succeeded === 1 ? '' : 's'} ${verb}d`);
        }
      },
    });
  };

  // Toggle a tab's "show inactive" flag: clears that tab's selection, resets its
  // pagination page (row set changes), and reloads the type with the new flag.
  const handleToggleShowInactive = (type, pagination) => {
    const next = !showInactive[type];
    setShowInactive((prev) => ({ ...prev, [type]: next }));
    setSelected((prev) => ({ ...prev, [type]: [] }));
    pagination?.resetPage?.();
    loadRefdataType(type, next);
  };

  // Toggle a single row's selection within a tab.
  const toggleSelected = (type, id) => {
    setSelected((prev) => {
      const current = prev[type] || [];
      const exists = current.includes(id);
      return { ...prev, [type]: exists ? current.filter((x) => x !== id) : [...current, id] };
    });
  };

  const buildTableColumns = (fieldColumns, type) => {
    const selectedIds = selected[type] || [];
    return [
      // Selection checkbox column — always present (stable layout) for admins, so the
      // bulk activate/deactivate selector is available regardless of the active filter.
      ...(canAdmin ? [{
        key: '_select',
        header: '',
        align: 'center',
        sx: { width: 44, px: 0 },
        render: (row) => (
          <Checkbox
            size="small"
            sx={{ p: 0.5 }}
            checked={selectedIds.includes(row.id)}
            onClick={(e) => e.stopPropagation()}
            onChange={() => toggleSelected(type, row.id)}
          />
        ),
      }] : []),
      ...fieldColumns.map((col) => ({
        key: col.field,
        header: col.header,
        sortable: true,
        getValue: (row) => row[col.field],
        render: col.render ? (row) => col.render(row[col.field], row) : undefined,
      })),
      {
        key: '_actions',
        header: 'Actions',
        align: 'center',
        render: (row) => (
          <>
            {canWrite && (
              <IconButton size="small" onClick={() => handleOpenDialog(type, row)}>
                <EditIcon fontSize="small" />
              </IconButton>
            )}
            {canAdmin && (
              row.active === false ? (
                <IconButton size="small" color="success" title="Reactivate" onClick={() => handleReactivate(type, row)}>
                  <RestoreIcon fontSize="small" />
                </IconButton>
              ) : (
                <IconButton size="small" color="error" title="Delete" onClick={() => handleDelete(type, row.id, row)}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              )
            )}
          </>
        ),
      },
    ];
  };

  const formatProgramAmount = (amount) => {
    const num = parseFloat(amount);
    if (isNaN(num)) return '0.00';
    return num.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  };

  const renderActiveChip = (active) => (
    <Chip
      label={active ? 'Active' : 'Inactive'}
      color={active ? 'success' : 'default'}
      size="small"
    />
  );

  // T3-065: Related GL account cell. A blank Related account inherits the corresponding
  // base (External) account at report time, so render a muted "→ uses External" hint
  // instead of an empty cell — but only when the base value actually exists to inherit
  // (both blank shows a plain dash, as an unconfigured product does elsewhere).
  const renderRelatedGlCell = (baseField) => (value, row) => {
    const related = (value ?? '').toString().trim();
    if (related) return related;
    const base = (row?.[baseField] ?? '').toString().trim();
    if (base) {
      return (
        <Typography component="span" variant="body2" sx={{ fontStyle: 'italic', color: 'text.disabled' }}>
          → uses External
        </Typography>
      );
    }
    return '—';
  };

  // Float inactive rows to the top when that tab's "show inactive" is on, so they're
  // easy to spot for review/restore; otherwise return data untouched. Stable within
  // each group, and overridden once the user applies an explicit column sort.
  const orderInactiveFirst = (type, data) => {
    if (!showInactive[type] || !Array.isArray(data)) return data;
    const inactive = data.filter((r) => r.active === false);
    const active = data.filter((r) => r.active !== false);
    return inactive.length ? [...inactive, ...active] : data;
  };

  // Shared tab header. Left: bulk Activate/Deactivate actions (shown when rows are
  // selected). Right: "Show inactive" toggle then the tab's own "Add" action.
  // `data` is the tab's current row array (used to resolve selected ids for bulk ops).
  const renderTabHeader = (type, addButton, pagination, data) => {
    const selectedCount = (selected[type] || []).length;
    return (
      <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
        {canAdmin && selectedCount > 0 && (
          <>
            <Button
              variant="outlined"
              color="success"
              size="small"
              startIcon={<RestoreIcon />}
              onClick={() => handleBulkSetActive(type, data, true)}
            >
              Activate ({selectedCount})
            </Button>
            <Button
              variant="outlined"
              color="error"
              size="small"
              startIcon={<DeleteIcon />}
              onClick={() => handleBulkSetActive(type, data, false)}
            >
              Deactivate ({selectedCount})
            </Button>
          </>
        )}
        <Box sx={{ flexGrow: 1 }} />
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={!!showInactive[type]}
              onChange={() => handleToggleShowInactive(type, pagination)}
            />
          }
          label="Show inactive"
        />
        {addButton}
      </Box>
    );
  };

  // Sub-feature tab filtering
  const allRefTabs = useMemo(() => [
    { feature: 'countries', label: 'Countries' },
    { feature: 'frequencies', label: 'Frequencies' },
    { feature: 'investment_types', label: 'Investment Types' },
    { feature: 'document_types', label: 'Document Types' },
    { feature: 'programs', label: 'Programs' },
    { feature: 'interest_rates', label: 'Interest Rates' },
    { feature: 'fx_rates', label: 'FX Rates' },
    // Broker fees and stamp duties are SEPARATE, ADDITIVE charges that merely share one reference
    // table (refdata_placement_fees_and_duty), discriminated by its `type` column. 4 dev contracts
    // carry both at once, with distinct rates and distinct GL pairs (Broker DR 4120 / CR 2120,
    // Duty DR 4130 / CR 2130). The contract wizard already presents them separately —
    // BrokerSection filters `type === 'Broker Fee'`, StampDutySection filters `'Stamp Duty'` — so
    // this screen was the ONLY place that mixed them, and the only place an admin could create a
    // row belonging to neither (see the type-seeding note on the forms below).
    //
    // ⚠ BOTH TABS DELIBERATELY REUSE THE `placement_fees` FEATURE KEY (user decision, 2026-09-09).
    // It is ONE administrative capability shown as two lists, so the authorization model must not
    // change because the presentation did: splitting the permission would silently take half the
    // screen from an admin who can maintain placement fees today, and would need every tenant's
    // moduleConfig revisited for a change that alters no capability. A tenant that has explicitly
    // disabled `placement_fees` loses both tabs together, which is correct.
    //
    // CONSEQUENCE: `feature` can no longer identify WHICH tab is active, and it can no longer be
    // the React key. Hence `kind` — used for the render branch and the key below.
    { feature: 'placement_fees', kind: 'brokerFee', label: 'Broker Fees' },
    { feature: 'placement_fees', kind: 'stampDuty', label: 'Stamp Duties' },
    { feature: 'vat_rates', label: 'VAT Rates' },
    { feature: 'collateral_types', label: 'Collateral Types' },
    { feature: 'narrations', label: 'Narrations' },
    { feature: 'wht_rules', label: 'WHT Rules' },
    { feature: 'commission_rates', label: 'Commission Rates' },
  ], []);

  const visibleTabs = useMemo(() =>
    allRefTabs.filter(t => isFeatureEnabled('reference_data', t.feature)),
    [allRefTabs, isFeatureEnabled]
  );

  // Clamp tab value if tabs were removed
  const safeTabValue = visibleTabs.length > 0 ? Math.min(tabValue, visibleTabs.length - 1) : 0;
  const activeFeature = visibleTabs[safeTabValue]?.feature;
  // Two tabs share the `placement_fees` feature (see allRefTabs), so `activeFeature` alone cannot
  // tell them apart — `activeKind` does. Undefined for every other tab, which is why the render
  // branches below still key off activeFeature and only the two fee tabs add a kind test.
  const activeKind = visibleTabs[safeTabValue]?.kind;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Reference Data Management</Typography>
      </Box>

      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={safeTabValue}
          onChange={(e, v) => setTabValue(v)}
          variant="scrollable"
          scrollButtons="auto"
        >
          {/* Key on feature+kind, not feature alone: the two fee tabs share one feature key and
              duplicate React keys would make the list unstable. */}
          {visibleTabs.map(t => (
            <Tab key={t.kind ? `${t.feature}:${t.kind}` : t.feature} label={t.label} />
          ))}
        </Tabs>
      </Paper>

      {/* Countries */}
      {activeFeature === 'countries' && (
      <Box>
        {renderTabHeader('countries',
          canWrite && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog('countries')}
            >
              Add Country
            </Button>
          ), countriesPagination, countries)}
        <DataTable
          columns={buildTableColumns([
            { field: 'country', header: 'Country' },
            { field: 'country_code', header: 'Code' },
            { field: 'currency_code', header: 'Currency' },
            { field: 'currency_symbol', header: 'Symbol' },
            { field: 'active', header: 'Status', render: renderActiveChip },
          ], 'countries')}
          data={orderInactiveFirst('countries', countries)}
          pagination={countriesPagination}
          sort={countriesSort}
          emptyMessage="No countries found"
        />
      </Box>
      )}

      {/* Frequencies */}
      {activeFeature === 'frequencies' && (
      <Box>
        {renderTabHeader('frequencies',
          canWrite && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog('frequencies')}
            >
              Add Frequency
            </Button>
          ), frequenciesPagination, frequencies)}
        <DataTable
          columns={buildTableColumns([
            { field: 'frequency', header: 'Frequency' },
            { field: 'months_value', header: 'Months' },
            { field: 'active', header: 'Status', render: renderActiveChip },
          ], 'frequencies')}
          data={orderInactiveFirst('frequencies', frequencies)}
          pagination={frequenciesPagination}
          sort={frequenciesSort}
          emptyMessage="No frequencies found"
        />
      </Box>
      )}

      {/* Investment Types */}
      {activeFeature === 'investment_types' && (
      <Box>
        {renderTabHeader('investmentTypes',
          canWrite && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog('investmentTypes')}
            >
              Add Investment Type
            </Button>
          ), investmentTypesPagination, investmentTypes)}
        <DataTable
          columns={buildTableColumns([
            { field: 'reference', header: 'Reference' },
            { field: 'type', header: 'Type' },
            { field: 'category', header: 'Category' },
            { field: 'cr_control_account', header: 'Control Account (Ext)' },
            { field: 'dr_expense_account', header: 'Expense Account (Ext)' },
            // T3-065: the Related pair overrides the base (external) pair for related-party
            // investors. When blank it inherits the base pair, so show a muted hint rather
            // than an empty cell (only when a base value exists to inherit).
            { field: 'related_cr_control_account', header: 'Control Account (Rel)', render: renderRelatedGlCell('cr_control_account') },
            { field: 'related_dr_expense_account', header: 'Expense Account (Rel)', render: renderRelatedGlCell('dr_expense_account') },
            { field: 'active', header: 'Status', render: renderActiveChip },
          ], 'investmentTypes')}
          data={orderInactiveFirst('investmentTypes', investmentTypes)}
          pagination={investmentTypesPagination}
          sort={investmentTypesSort}
          emptyMessage="No investment types found"
        />
      </Box>
      )}

      {/* Document Types */}
      {activeFeature === 'document_types' && (
      <Box>
        {renderTabHeader('documentTypes',
          canWrite && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog('documentTypes')}
            >
              Add Document Type
            </Button>
          ), documentTypesPagination, documentTypes)}
        <DataTable
          columns={buildTableColumns([
            { field: 'code', header: 'Code' },
            { field: 'display_name', header: 'Display Name' },
            { field: 'description', header: 'Description' },
            { field: 'is_submission_document', header: 'Submission Doc', render: (v) => (v ? 'Yes' : '—') },
            { field: 'per_investor', header: 'Per Investor', render: (v) => (v ? 'Yes' : '—') },
            { field: 'validity_months', header: 'Validity (Months)' },
            { field: 'active', header: 'Status', render: renderActiveChip },
            { field: 'is_system', header: 'Type', render: (v) => (v ? 'System' : 'Custom') },
          ], 'documentTypes')}
          data={orderInactiveFirst('documentTypes', documentTypes)}
          pagination={documentTypesPagination}
          sort={documentTypesSort}
          emptyMessage="No document types found"
        />
      </Box>
      )}

      {/* Programs (capped MTN programs) */}
      {activeFeature === 'programs' && (
      <Box>
        {renderTabHeader('programs',
          canWrite && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog('programs')}
            >
              Add Program
            </Button>
          ), programsPagination, programs)}
        <DataTable
          columns={buildTableColumns([
            { field: 'name', header: 'Program' },
            { field: 'issuer_name', header: 'Issuer' },
            { field: 'investment_types', header: 'Types', render: (v) => (
              Array.isArray(v) && v.length
                ? <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {v.map((t) => <Chip key={t.id} label={t.reference} size="small" />)}
                  </Box>
                : '—'
            ) },
            { field: 'currency_code', header: 'Currency' },
            { field: 'limit_amount', header: 'Limit', render: (v) => (v === null || v === undefined ? 'No limit' : formatProgramAmount(v)) },
            { field: 'used_capital', header: 'Used', render: (v) => formatProgramAmount(v) },
            { field: 'available', header: 'Available', render: (v, row) => (row.limit_amount === null || row.limit_amount === undefined ? '—' : formatProgramAmount(v)) },
            { field: 'expiry_date', header: 'Expires', render: (v) => (v ? new Date(v).toLocaleDateString('en-GB') : '—') },
            { field: 'is_diminishing', header: 'Mode', render: (v) => (v ? <Chip label="Diminishing" color="warning" size="small" /> : <Chip label="Re-usable" size="small" />) },
            { field: 'listed', header: 'Listed', render: (v) => (v ? <Chip label="Listed" color="primary" size="small" /> : <Chip label="Unlisted" size="small" />) },
            { field: 'active', header: 'Status', render: renderActiveChip },
          ], 'programs')}
          data={orderInactiveFirst('programs', programs)}
          pagination={programsPagination}
          sort={programsSort}
          emptyMessage="No programs found"
        />
      </Box>
      )}

      {/* Interest Rates */}
      {activeFeature === 'interest_rates' && (
      <Box>
        {renderTabHeader('interestRates',
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            {canWrite && (
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={promptImpact}
                    onChange={(e) => {
                      setPromptImpact(e.target.checked);
                      saveImpactPromptPreference(e.target.checked);
                    }}
                  />
                }
                label={<Typography variant="body2">Prompt for impact assessment</Typography>}
                title="When off, saving a rate won't interrupt with the impact assessment prompt - run it once afterwards with the button alongside."
                sx={{ mr: 0 }}
              />
            )}
            <Button
              variant="outlined"
              startIcon={<AssessmentIcon />}
              onClick={() => navigate('/rate-changes/impact')}
            >
              Impact Assessment
            </Button>
            {canWrite && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => handleOpenDialog('interestRates')}
              >
                Add Interest Rate
              </Button>
            )}
          </Box>, null, interestRates)}
        {/* Country sub-tabs and interest rates grouped by type */}
        {(() => {
          // Derive unique countries that have interest rates
          const countriesWithRates = [];
          const seenCountryIds = new Set();
          interestRates.forEach(rate => {
            if (rate.country_id && !seenCountryIds.has(rate.country_id)) {
              seenCountryIds.add(rate.country_id);
              countriesWithRates.push({
                country_id: rate.country_id,
                country_name: rate.country_name,
                currency_code: rate.currency_code,
              });
            }
          });
          countriesWithRates.sort((a, b) => (a.country_name || '').localeCompare(b.country_name || ''));

          // Filter rates by selected country sub-tab (0 = All Countries)
          const filteredRates = interestRateCountryTab === 0
            ? interestRates
            : interestRates.filter(r => r.country_id === countriesWithRates[interestRateCountryTab - 1]?.country_id);

          // Group filtered rates by interest_rate_type
          const groupedRates = filteredRates.reduce((acc, rate) => {
            const type = rate.interest_rate_type || 'Unknown';
            if (!acc[type]) acc[type] = [];
            acc[type].push(rate);
            return acc;
          }, {});

          // Sort each group ACTIVE-FIRST, then effective_date descending, then id descending.
          //
          // Active-first is what makes "row 0" mean "the rate actually in force". With Show inactive
          // on, a pure date sort let a DEACTIVATED newest-dated row take the Current badge, the
          // highlight and the only Edit pencil while its own Status cell read "Inactive" — the row
          // contradicted itself, and the live rate below it could not be edited. The API applies the
          // same active-first rule when it decides which rate is editable, so the two must agree.
          //
          // The id tiebreak mirrors fn_refdata_interest_rates_get_all's ORDER BY (T3-099): two rows
          // can share an effective_date, so without it the badge could move between page loads.
          Object.keys(groupedRates).forEach(type => {
            groupedRates[type].sort((a, b) =>
              ((a.active === false) - (b.active === false))
              || (new Date(b.effective_date) - new Date(a.effective_date))
              || (b.id - a.id)
            );
          });

          return (
            <>
              {/* Country sub-tabs */}
              {countriesWithRates.length > 0 && (
                <Paper sx={{ mb: 3 }}>
                  <Tabs
                    value={interestRateCountryTab}
                    onChange={(e, v) => setInterestRateCountryTab(v)}
                    variant="scrollable"
                    scrollButtons="auto"
                  >
                    <Tab label="All Countries" {...a11yProps(0, 'ir-country')} />
                    {countriesWithRates.map((country, index) => (
                      <Tab
                        key={country.country_id}
                        label={`${country.country_name} (${country.currency_code})`}
                        {...a11yProps(index + 1, 'ir-country')}
                      />
                    ))}
                  </Tabs>
                </Paper>
              )}

              {/* Rate types are per-country, so a card on this tab can span more than one country.
                  Adding is done from a country tab, where the target group is unambiguous. */}
              {canWrite && interestRateCountryTab === 0 && countriesWithRates.length > 0 && (
                <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 2 }}>
                  Select a country tab to add a rate or load historic rates for a specific rate type.
                </Typography>
              )}

              {/* Rate cards grouped by type */}
              {Object.keys(groupedRates).length === 0 ? (
                <Paper sx={{ p: 3, textAlign: 'center' }}>
                  <Typography color="textSecondary">No interest rates found</Typography>
                </Paper>
              ) : (
                Object.entries(groupedRates).map(([rateType, rates]) => {
                  // The CURRENT rate is the newest ACTIVE one, which the active-first sort above puts
                  // at index 0 — so this is 0 whenever the group has any active rate, and -1 when
                  // every rate in it has been deactivated. Everything that marks a row as "current"
                  // (highlight, badge, bold, Edit) keys off this rather than a bare index === 0, so a
                  // deactivated row can never present itself as the rate in force.
                  const currentIdx = rates.findIndex(r => r.active !== false);
                  const hasCurrent = currentIdx !== -1;
                  // Falls back to rates[0] purely to keep the group's country/institution seed usable
                  // when nothing is active; the header chip is suppressed in that case (see below).
                  const latestRate = hasCurrent ? rates[currentIdx] : rates[0];
                  // A rate "group" is the derived tuple (country_id, interest_rate_type) - there is
                  // no rate-type entity. On the All Countries tab a type used by two countries
                  // collapses into one card, so latestRate can belong to a different country than
                  // other rows; the per-group Add buttons are therefore hidden on that tab (below)
                  // rather than seeding an ambiguous country.
                  const isSingleCountry = interestRateCountryTab !== 0;
                  const groupSeed = {
                    country_id: latestRate.country_id,
                    interest_rate_type: rateType,
                    prime_rate_institution: latestRate.prime_rate_institution || '',
                    is_variable: latestRate.is_variable === true,
                    prime_rate: latestRate.prime_rate ?? null,
                    country_label: `${latestRate.country_name}${latestRate.currency_code ? ` (${latestRate.currency_code})` : ''}`,
                  };
                  return (
                    <Card key={rateType} sx={{ mb: 3 }}>
                      <CardContent sx={{ pb: 1 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 2, flexWrap: 'wrap' }}>
                          <Box>
                            <Typography variant="h6" component="span">
                              {rateType}
                            </Typography>
                            {/* Only assert a current rate when one is actually active. A group whose
                                every rate is deactivated has no rate in force, so claiming one would
                                be false — say so instead. */}
                            <Chip
                              label={hasCurrent
                                ? `Current: ${latestRate.effective_interest_rate}%`
                                : 'No active rate'}
                              color={hasCurrent ? 'primary' : 'default'}
                              size="small"
                              sx={{ ml: 2, fontWeight: 'bold' }}
                            />
                            {isSingleCountry && (
                              <Typography variant="body2" color="textSecondary" sx={{ mt: 0.5 }}>
                                {groupSeed.country_label}
                              </Typography>
                            )}
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="caption" color="textSecondary">
                              {rates.length} historical rate{rates.length !== 1 ? 's' : ''}
                            </Typography>
                            {canWrite && isSingleCountry && (
                              <>
                                <Button
                                  size="small"
                                  startIcon={<AddIcon />}
                                  onClick={() => handleOpenDialog('interestRates', null, groupSeed)}
                                  title={`Add a rate to ${rateType}`}
                                >
                                  Add rate
                                </Button>
                                <Button
                                  size="small"
                                  startIcon={<HistoryIcon />}
                                  onClick={() => handleOpenBatchDialog(groupSeed)}
                                  title={`Load several historic rates for ${rateType} at once`}
                                >
                                  Add history
                                </Button>
                              </>
                            )}
                          </Box>
                        </Box>
                      </CardContent>
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                              <TableCell>Effective Date</TableCell>
                              <TableCell align="right">Rate %</TableCell>
                              <TableCell>Institution</TableCell>
                              <TableCell>Status</TableCell>
                              <TableCell align="center">Actions</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {rates.map((rate, index) => {
                              // "Is this the rate in force?" — the newest ACTIVE row, not merely the
                              // first one listed. False for every row when the whole group is inactive.
                              const isCurrent = index === currentIdx;
                              return (
                              <TableRow
                                key={rate.id}
                                sx={{
                                  backgroundColor: isCurrent ? '#e3f2fd' : 'inherit',
                                  '&:hover': { backgroundColor: isCurrent ? '#bbdefb' : '#f5f5f5' }
                                }}
                              >
                                <TableCell>
                                  {rate.effective_date ? new Date(rate.effective_date).toLocaleDateString('en-GB', {
                                    day: '2-digit',
                                    month: 'short',
                                    year: 'numeric'
                                  }) : '-'}
                                  {isCurrent && (
                                    <Chip label="Current" size="small" color="success" sx={{ ml: 1, height: 20 }} />
                                  )}
                                </TableCell>
                                <TableCell align="right">
                                  <Typography fontWeight={isCurrent ? 'bold' : 'normal'}>
                                    {rate.effective_interest_rate}%
                                  </Typography>
                                </TableCell>
                                <TableCell>{rate.prime_rate_institution || '-'}</TableCell>
                                <TableCell>{renderActiveChip(rate.active)}</TableCell>
                                <TableCell align="center">
                                  {canWrite && isCurrent && (
                                    <IconButton
                                      size="small"
                                      onClick={() => handleOpenDialog('interestRates', rate)}
                                      title="Edit current rate"
                                    >
                                      <EditIcon fontSize="small" />
                                    </IconButton>
                                  )}
                                  {canAdmin && (
                                    rate.active === false ? (
                                      <IconButton
                                        size="small"
                                        color="success"
                                        title="Reactivate"
                                        onClick={() => handleReactivate('interestRates', rate)}
                                      >
                                        <RestoreIcon fontSize="small" />
                                      </IconButton>
                                    ) : (
                                      <>
                                        {/* Deactivate must be reachable per-row. Delete is refused for a
                                            rate any contract or rate change is tied to (T3-095) and the
                                            refusal says "Deactivate it instead" — but this card view has
                                            no checkboxes, so the bulk Deactivate button in the tab header
                                            can never appear here, and the edit pencil only renders for
                                            the current rate. Without this control a tied, superseded rate
                                            had no reachable action at all. */}
                                        <IconButton
                                          size="small"
                                          color="warning"
                                          title="Deactivate (keeps the rate for contracts already using it)"
                                          onClick={() => handleDeactivate('interestRates', rate)}
                                        >
                                          <BlockIcon fontSize="small" />
                                        </IconButton>
                                        <IconButton
                                          size="small"
                                          color="error"
                                          title="Delete"
                                          onClick={() => handleDelete('interestRates', rate.id, rate)}
                                        >
                                          <DeleteIcon fontSize="small" />
                                        </IconButton>
                                      </>
                                    )
                                  )}
                                </TableCell>
                              </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Card>
                  );
                })
              )}
            </>
          );
        })()}
      </Box>
      )}

      {/* FX Rates */}
      {activeFeature === 'fx_rates' && (
      <Box>
        {renderTabHeader('fxRates',
          canWrite && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog('fxRates')}
            >
              Add FX Rate
            </Button>
          ), fxRatesPagination, fxRates)}
        <DataTable
          columns={buildTableColumns([
            { field: 'currency_pair', header: 'Currency Pair' },
            { field: 'rate', header: 'Rate' },
            { field: 'effective_date', header: 'Effective Date' },
            { field: 'source', header: 'Source' },
            { field: 'active', header: 'Status', render: renderActiveChip },
          ], 'fxRates')}
          data={orderInactiveFirst('fxRates', fxRates)}
          pagination={fxRatesPagination}
          sort={fxRatesSort}
          emptyMessage="No FX rates found"
        />
      </Box>
      )}

      {/* Broker Fees / Stamp Duties — two tabs over ONE shared reference table.
          Both filter `refData.placementFees` client-side by `type`: the feed is 4 rows and is
          already cached (5-min TTL, tenant-scoped, in-flight dedupe), so two tabs reading one
          array is cheaper than two round trips, needs no API change, and leaves the 5 CRUD
          functions/routes untouched.
          The `Type` column is gone from both — the tab IS the type, so showing it would be
          redundant, and the Add form seeds it rather than asking (see PLACEMENT_FEE_KINDS). */}
      {activeFeature === 'placement_fees' && PLACEMENT_FEE_KINDS[activeKind] && (
      <Box>
        {renderTabHeader('placementFees',
          canWrite && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog('placementFees', null, { type: PLACEMENT_FEE_KINDS[activeKind].type })}
            >
              {PLACEMENT_FEE_KINDS[activeKind].addLabel}
            </Button>
          ), placementFeesPagination, placementFees)}
        <DataTable
          columns={buildTableColumns([
            { field: 'reference', header: 'Reference' },
            { field: 'percentage', header: 'Percentage %', render: (v) => v != null ? `${formatRatePct(v)}%` : '-' },
            { field: 'applicable_products', header: 'Products' },
            { field: 'dr_expense_account', header: 'Expense Account' },
            { field: 'cr_control_account', header: 'Control Account' },
            { field: 'active', header: 'Status', render: renderActiveChip },
          ], 'placementFees')}
          data={orderInactiveFirst(
            'placementFees',
            (placementFees || []).filter(f => f.type === PLACEMENT_FEE_KINDS[activeKind].type)
          )}
          pagination={placementFeesPagination}
          sort={placementFeesSort}
          emptyMessage={PLACEMENT_FEE_KINDS[activeKind].emptyMessage}
        />
      </Box>
      )}

      {/* VAT Rates */}
      {activeFeature === 'vat_rates' && (
      <Box>
        {renderTabHeader('vatRates',
          canWrite && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog('vatRates')}
            >
              Add VAT Rate
            </Button>
          ), vatRatesPagination, vatRates)}
        <DataTable
          columns={buildTableColumns([
            { field: 'country_name', header: 'Country' },
            { field: 'currency_code', header: 'Currency' },
            { field: 'vat_rate', header: 'VAT Rate %', render: (v) => v != null ? `${formatRatePct(v)}%` : '-' },
            { field: 'description', header: 'Description' },
            { field: 'active', header: 'Status', render: renderActiveChip },
          ], 'vatRates')}
          data={orderInactiveFirst('vatRates', vatRates)}
          pagination={vatRatesPagination}
          sort={vatRatesSort}
          emptyMessage="No VAT rates found"
        />
      </Box>
      )}

      {/* Collateral Types */}
      {activeFeature === 'collateral_types' && (
      <Box>
        {renderTabHeader('collateralTypes',
          canWrite && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog('collateralTypes')}
            >
              Add Collateral Type
            </Button>
          ), collateralTypesPagination, collateralTypes)}
        <DataTable
          columns={buildTableColumns([
            { field: 'type', header: 'Type' },
            { field: 'description', header: 'Description' },
            { field: 'active', header: 'Status', render: renderActiveChip },
          ], 'collateralTypes')}
          data={orderInactiveFirst('collateralTypes', collateralTypes)}
          pagination={collateralTypesPagination}
          sort={collateralTypesSort}
          emptyMessage="No collateral types found"
        />
      </Box>
      )}

      {/* Narrations */}
      {activeFeature === 'narrations' && (
      <Box>
        {renderTabHeader('narrations',
          canWrite && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog('narrations')}
            >
              Add Narration
            </Button>
          ), narrationsPagination, narrations)}
        <DataTable
          columns={buildTableColumns([
            { field: 'narration', header: 'Narration' },
            { field: 'classification', header: 'Classification' },
            { field: 'multiplier', header: 'Multiplier' },
            { field: 'active', header: 'Status', render: renderActiveChip },
          ], 'narrations')}
          data={orderInactiveFirst('narrations', narrations)}
          pagination={narrationsPagination}
          sort={narrationsSort}
          emptyMessage="No narrations found"
        />
      </Box>
      )}

      {/* WHT Rules */}
      {activeFeature === 'wht_rules' && (
      <Box>
        {renderTabHeader('whtRules',
          canWrite && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog('whtRules')}
            >
              Add WHT Rule
            </Button>
          ), whtRulesPagination, whtRules)}
        <DataTable
          columns={buildTableColumns([
            {
              field: 'tax_classification',
              header: 'Tax Classification',
              render: (val) => <Chip label={val} size="small" variant="outlined" />,
            },
            {
              field: 'country_name',
              header: 'Country',
              render: (val) => val || 'All Countries',
            },
            {
              field: 'wht_rate',
              header: 'WHT Rate',
              render: (val) => val != null ? `${val}%` : '-',
            },
            { field: 'description', header: 'Description' },
            {
              field: 'effective_from',
              header: 'Effective From',
              render: (val) => val ? new Date(val).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '-',
            },
            {
              field: 'effective_to',
              header: 'Effective To',
              render: (val) => val ? new Date(val).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : 'No End Date',
            },
            { field: 'active', header: 'Status', render: renderActiveChip },
          ], 'whtRules')}
          data={orderInactiveFirst('whtRules', whtRules)}
          pagination={whtRulesPagination}
          sort={whtRulesSort}
          emptyMessage="No WHT rules found"
        />
      </Box>
      )}

      {/* Commission Rates */}
      {activeFeature === 'commission_rates' && (
      <Box>
        {renderTabHeader('commissionRates',
          canWrite && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog('commissionRates')}
            >
              Add Commission Rate
            </Button>
          ), commissionRatesPagination, commissionRates)}
        <DataTable
          columns={buildTableColumns([
            {
              field: 'broker_name',
              header: 'Broker',
              render: (val) => val || 'All Brokers',
            },
            {
              field: 'investor_name',
              header: 'Investor',
              render: (val) => val || 'All Investors',
            },
            {
              field: 'rate_type',
              header: 'Rate Type',
              render: (val) => {
                const colorMap = { spread: 'primary', flat_fee: 'secondary', tiered: 'warning' };
                return <Chip label={val} size="small" color={colorMap[val] || 'default'} />;
              },
            },
            {
              field: 'rate',
              header: 'Rate',
              render: (val) => val != null ? `${val}%` : '-',
            },
            {
              field: 'min_amount',
              header: 'Min Amount',
              render: (val) => val != null ? formatNumber(Number(val), 2) : '-',
            },
            {
              field: 'max_amount',
              header: 'Max Amount',
              render: (val) => val != null ? formatNumber(Number(val), 2) : '-',
            },
            {
              field: 'effective_from',
              header: 'Effective From',
              render: (val) => val ? new Date(val).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '-',
            },
            { field: 'active', header: 'Status', render: renderActiveChip },
          ], 'commissionRates')}
          data={orderInactiveFirst('commissionRates', commissionRates)}
          pagination={commissionRatesPagination}
          sort={commissionRatesSort}
          emptyMessage="No commission rates found"
        />
      </Box>
      )}

      {/* Generic Add/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {dialogSeed?.interest_rate_type
            ? `Add Rate — ${dialogSeed.interest_rate_type}`
            : `${editingItem ? 'Edit' : 'Add'} Reference Data`}
        </DialogTitle>
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogContent>
            {submitError && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSubmitError(null)}>
                {submitError}
              </Alert>
            )}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
              {/* Countries Form */}
              {dataType === 'countries' && (
                <>
                  <Controller
                    name="country"
                    control={control}
                    defaultValue=""
                    rules={{ required: 'Country is required' }}
                    render={({ field, fieldState: { error } }) => (
                      <TextField
                        {...field}
                        label="Country"
                        fullWidth
                        required
                        error={!!error}
                        helperText={error?.message}
                      />
                    )}
                  />
                  <Controller
                    name="country_code"
                    control={control}
                    defaultValue=""
                    rules={{ required: 'Country code is required' }}
                    render={({ field, fieldState: { error } }) => (
                      <TextField
                        {...field}
                        label="Country Code"
                        fullWidth
                        required
                        error={!!error}
                        helperText={error?.message}
                      />
                    )}
                  />
                  <Controller
                    name="currency_code"
                    control={control}
                    defaultValue=""
                    rules={{ required: 'Currency code is required' }}
                    render={({ field, fieldState: { error } }) => (
                      <TextField
                        {...field}
                        label="Currency Code"
                        fullWidth
                        required
                        error={!!error}
                        helperText={error?.message}
                      />
                    )}
                  />
                  <Controller
                    name="currency_symbol"
                    control={control}
                    defaultValue=""
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Currency Symbol"
                        fullWidth
                      />
                    )}
                  />
                  <Controller
                    name="active"
                    control={control}
                    defaultValue={true}
                    render={({ field }) => (
                      <FormControlLabel
                        control={<Switch {...field} checked={field.value} />}
                        label="Active"
                      />
                    )}
                  />
                </>
              )}

              {/* Frequencies Form */}
              {dataType === 'frequencies' && (
                <>
                  <Controller
                    name="frequency"
                    control={control}
                    defaultValue=""
                    rules={{ required: 'Frequency is required' }}
                    render={({ field, fieldState: { error } }) => (
                      <TextField
                        {...field}
                        label="Frequency"
                        fullWidth
                        required
                        error={!!error}
                        helperText={error?.message}
                      />
                    )}
                  />
                  <Controller
                    name="months_value"
                    control={control}
                    defaultValue=""
                    rules={{ required: 'Months value is required' }}
                    render={({ field, fieldState: { error } }) => (
                      <TextField
                        {...field}
                        label="Months Value"
                        type="number"
                        fullWidth
                        required
                        error={!!error}
                        helperText={error?.message}
                      />
                    )}
                  />
                  <Controller
                    name="active"
                    control={control}
                    defaultValue={true}
                    render={({ field }) => (
                      <FormControlLabel
                        control={<Switch {...field} checked={field.value} />}
                        label="Active"
                      />
                    )}
                  />
                </>
              )}

              {/* Investment Types Form */}
              {dataType === 'investmentTypes' && (
                <>
                  <Controller
                    name="reference"
                    control={control}
                    defaultValue=""
                    rules={{ required: 'Reference is required' }}
                    render={({ field, fieldState: { error } }) => (
                      <TextField
                        {...field}
                        label="Reference"
                        fullWidth
                        required
                        error={!!error}
                        helperText={error?.message}
                      />
                    )}
                  />
                  <Controller
                    name="type"
                    control={control}
                    defaultValue=""
                    rules={{ required: 'Type is required' }}
                    render={({ field, fieldState: { error } }) => (
                      <TextField
                        {...field}
                        label="Type"
                        fullWidth
                        required
                        error={!!error}
                        helperText={error?.message}
                      />
                    )}
                  />
                  <Controller
                    name="category"
                    control={control}
                    defaultValue=""
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Category"
                        fullWidth
                      />
                    )}
                  />
                  {/* T3-065: two GL account pairs. The External (base) pair is the default
                      and mandatory fallback; the Related pair overrides it for related-party
                      investors and inherits the External pair when left blank. The correct
                      pair is chosen per journal/ledger line from the investor's Related Party
                      flag. */}
                  <Typography variant="subtitle2" sx={{ mt: 1 }}>
                    GL accounts — External investors (default)
                  </Typography>
                  <Controller
                    name="cr_control_account"
                    control={control}
                    defaultValue=""
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Control Account (External)"
                        fullWidth
                        placeholder="e.g., 2100-001"
                      />
                    )}
                  />
                  <Controller
                    name="dr_expense_account"
                    control={control}
                    defaultValue=""
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Expense Account (External)"
                        fullWidth
                        placeholder="e.g., 5100-001"
                      />
                    )}
                  />
                  <Typography variant="subtitle2" sx={{ mt: 1 }}>
                    GL accounts — Related-party investors (optional)
                  </Typography>
                  <Controller
                    name="related_cr_control_account"
                    control={control}
                    defaultValue=""
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Control Account (Related)"
                        fullWidth
                        placeholder="e.g., 2100-101"
                        helperText="Leave blank to use the External Control Account"
                      />
                    )}
                  />
                  <Controller
                    name="related_dr_expense_account"
                    control={control}
                    defaultValue=""
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Expense Account (Related)"
                        fullWidth
                        placeholder="e.g., 5100-101"
                        helperText="Leave blank to use the External Expense Account"
                      />
                    )}
                  />
                  <Controller
                    name="active"
                    control={control}
                    defaultValue={true}
                    render={({ field }) => (
                      <FormControlLabel
                        control={<Switch {...field} checked={field.value} />}
                        label="Active"
                      />
                    )}
                  />

                  {/* Per-product documents required at submit-for-approval. One
                      switch per candidate submission-document type (configured on
                      the Document Types tab). Turn off the ones this product does
                      not need. */}
                  <Box sx={{ mt: 1, pt: 1, borderTop: '1px solid', borderColor: 'divider' }}>
                    <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                      Documents required at submit for approval
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                      Controls which documents must be attached when a contract of this product is submitted for approval.
                    </Typography>
                    {docReqs.length === 0 ? (
                      <Typography variant="body2" color="text.secondary">
                        No submission document types are configured. Add one on the Document Types tab (mark it as a submission document).
                      </Typography>
                    ) : (
                      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        {docReqs.map((r) => (
                          <FormControlLabel
                            key={r.document_type_code}
                            control={
                              <Switch
                                checked={r.is_required}
                                onChange={(e) => setDocReqRequired(r.document_type_code, e.target.checked)}
                              />
                            }
                            label={r.per_investor ? `${r.document_type_name} (per investor)` : r.document_type_name}
                          />
                        ))}
                      </Box>
                    )}
                  </Box>
                </>
              )}

              {/* Document Types Form */}
              {dataType === 'documentTypes' && (
                <>
                  <Controller
                    name="code"
                    control={control}
                    defaultValue=""
                    rules={{ required: 'Code is required' }}
                    render={({ field, fieldState: { error } }) => (
                      <TextField
                        {...field}
                        label="Code"
                        fullWidth
                        required
                        disabled={!!editingItem}
                        error={!!error}
                        helperText={error?.message || 'Unique identifier (lowercase, underscores only)'}
                        placeholder="e.g., board_resolution"
                        onChange={(e) => field.onChange(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_'))}
                      />
                    )}
                  />
                  <Controller
                    name="display_name"
                    control={control}
                    defaultValue=""
                    rules={{ required: 'Display name is required' }}
                    render={({ field, fieldState: { error } }) => (
                      <TextField {...field} label="Display Name" fullWidth required error={!!error} helperText={error?.message} placeholder="e.g., Board Resolution" />
                    )}
                  />
                  <Controller
                    name="description"
                    control={control}
                    defaultValue=""
                    render={({ field }) => (
                      <TextField {...field} label="Description" fullWidth multiline rows={2} />
                    )}
                  />
                  <Controller
                    name="validity_months"
                    control={control}
                    defaultValue=""
                    render={({ field }) => (
                      <TextField {...field} label="Validity (Months)" type="number" fullWidth helperText="Leave empty for no expiry" />
                    )}
                  />
                  <Controller
                    name="sort_order"
                    control={control}
                    defaultValue={50}
                    render={({ field }) => (
                      <TextField {...field} label="Sort Order" type="number" fullWidth helperText="Lower numbers appear first" />
                    )}
                  />
                  <Controller
                    name="is_submission_document"
                    control={control}
                    defaultValue={false}
                    render={({ field }) => (
                      <FormControlLabel
                        control={<Switch {...field} checked={!!field.value} />}
                        label="Required at submit for approval (candidate document)"
                      />
                    )}
                  />
                  <Controller
                    name="per_investor"
                    control={control}
                    defaultValue={false}
                    render={({ field }) => (
                      <FormControlLabel
                        control={<Switch {...field} checked={!!field.value} />}
                        label="Per investor (one document per investor)"
                      />
                    )}
                  />
                  <Controller
                    name="is_active"
                    control={control}
                    defaultValue={true}
                    render={({ field }) => (
                      <FormControlLabel
                        control={<Switch {...field} checked={!!field.value} />}
                        label="Active (visible in upload forms)"
                      />
                    )}
                  />
                </>
              )}

              {/* Programs Form (capped MTN programs) */}
              {dataType === 'programs' && (
                <>
                  <Controller
                    name="name"
                    control={control}
                    defaultValue=""
                    rules={{ required: 'Program name is required' }}
                    render={({ field, fieldState: { error } }) => (
                      <TextField
                        {...field}
                        label="Program Name"
                        fullWidth
                        required
                        placeholder="e.g., Select E1bn Listed MTN"
                        error={!!error}
                        helperText={error?.message}
                      />
                    )}
                  />
                  <Controller
                    name="issuer_id"
                    control={control}
                    defaultValue=""
                    rules={{ required: 'Issuer is required' }}
                    render={({ field, fieldState: { error } }) => (
                      <FormControl fullWidth required error={!!error}>
                        <InputLabel>Issuer</InputLabel>
                        <Select {...field} label="Issuer" value={field.value || ''}>
                          {issuersList.map((iss) => (
                            <MenuItem key={iss.id} value={iss.id}>{iss.name}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                  />
                  <Controller
                    name="investment_type_ids"
                    control={control}
                    defaultValue={[]}
                    rules={{ validate: (v) => (Array.isArray(v) && v.length > 0) || 'At least one investment type is required' }}
                    render={({ field, fieldState: { error } }) => (
                      <FormControl fullWidth required error={!!error}>
                        <InputLabel>Investment Types</InputLabel>
                        <Select
                          {...field}
                          multiple
                          label="Investment Types"
                          value={Array.isArray(field.value) ? field.value : []}
                          renderValue={(selected) => (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                              {selected.map((id) => {
                                const t = investmentTypes.find((x) => x.id === id);
                                return <Chip key={id} label={t ? t.reference : id} size="small" />;
                              })}
                            </Box>
                          )}
                        >
                          {investmentTypes.filter(t => t.active !== false).map((t) => (
                            <MenuItem key={t.id} value={t.id}>{t.reference} — {t.type}</MenuItem>
                          ))}
                        </Select>
                        {error && <FormHelperText>{error.message}</FormHelperText>}
                      </FormControl>
                    )}
                  />
                  <Controller
                    name="currency_id"
                    control={control}
                    defaultValue=""
                    rules={{ required: 'Currency is required' }}
                    render={({ field, fieldState: { error } }) => (
                      <FormControl fullWidth required error={!!error}>
                        <InputLabel>Currency</InputLabel>
                        <Select {...field} label="Currency" value={field.value || ''}>
                          {countries.map((c) => (
                            <MenuItem key={c.id} value={c.id}>{c.currency_code} ({c.country})</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                  />
                  <Controller
                    name="limit_amount"
                    control={control}
                    defaultValue=""
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Limit Amount"
                        type="number"
                        fullWidth
                        placeholder="Leave blank for no limit"
                        helperText="Maximum committed capital under this program. Blank = no limit."
                      />
                    )}
                  />
                  <Controller
                    name="expiry_date"
                    control={control}
                    defaultValue=""
                    render={({ field }) => (
                      <TextField
                        {...field}
                        value={field.value || ''}
                        label="Expiry Date"
                        type="date"
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                        helperText="Optional — after this date no new contracts can be added and existing contracts cannot have their capital increased."
                      />
                    )}
                  />
                  <Controller
                    name="is_diminishing"
                    control={control}
                    defaultValue={false}
                    render={({ field }) => (
                      <FormControlLabel
                        control={<Switch {...field} checked={!!field.value} />}
                        label="Diminishing"
                      />
                    )}
                  />
                  <Controller
                    name="listed"
                    control={control}
                    defaultValue={false}
                    render={({ field }) => (
                      <FormControlLabel
                        control={<Switch {...field} checked={!!field.value} />}
                        label="Listed"
                      />
                    )}
                  />
                  <Controller
                    name="active"
                    control={control}
                    defaultValue={true}
                    render={({ field }) => (
                      <FormControlLabel
                        control={<Switch {...field} checked={field.value} />}
                        label="Active"
                      />
                    )}
                  />
                </>
              )}

              {/* Interest Rates Form */}
              {dataType === 'interestRates' && (
                <>
                  {/* Opened from a specific group card: the group is fixed, so show it as context
                      rather than re-asking for country + type. The Controllers below are NOT
                      rendered in this case - their seeded values stay in react-hook-form (default
                      shouldUnregister: false) and still submit. Do NOT "improve" this by rendering
                      them with `disabled` on the Controller: RHF strips disabled fields from the
                      submit payload, which drops country_id (API 400) and silently prevents the
                      impact prompt from firing. */}
                  {dialogSeed ? (
                    <Box sx={{ bgcolor: 'grey.50', border: 1, borderColor: 'divider', borderRadius: 1, p: 1.5 }}>
                      <Typography variant="caption" color="textSecondary" display="block">
                        Adding to rate type
                      </Typography>
                      <Typography variant="subtitle1" fontWeight="bold">
                        {dialogSeed.interest_rate_type}
                      </Typography>
                      {dialogSeed.country_label && (
                        <Typography variant="body2" color="textSecondary">
                          {dialogSeed.country_label}
                        </Typography>
                      )}
                    </Box>
                  ) : (
                    <>
                      <Controller
                        name="country_id"
                        control={control}
                        defaultValue=""
                        rules={{ required: 'Country is required' }}
                        render={({ field, fieldState: { error } }) => (
                          <TextField
                            {...field}
                            select
                            label="Country"
                            fullWidth
                            required
                            error={!!error}
                            helperText={error?.message}
                          >
                            {countries.map((country) => (
                              <MenuItem key={country.id} value={country.id}>
                                {country.country} ({country.currency_code})
                              </MenuItem>
                            ))}
                          </TextField>
                        )}
                      />
                      <Controller
                        name="interest_rate_type"
                        control={control}
                        defaultValue=""
                        rules={{ required: 'Rate type is required' }}
                        render={({ field, fieldState: { error } }) => (
                          <Autocomplete
                            value={field.value || ''}
                            freeSolo
                            options={[...new Set(
                              interestRates
                                .filter(r => !watchedCountryId || r.country_id === watchedCountryId)
                                .map(r => r.interest_rate_type)
                            )].filter(Boolean)}
                            onChange={(_, value) => field.onChange(value || '')}
                            onInputChange={(_, value) => field.onChange(value || '')}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Interest Rate Type"
                                required
                                error={!!error}
                                helperText={error?.message || 'Select existing or type new (e.g., Prime, TBOI, FNB)'}
                              />
                            )}
                          />
                        )}
                      />
                    </>
                  )}
                  <Controller
                    name="effective_interest_rate"
                    control={control}
                    defaultValue=""
                    rules={{ required: 'Rate is required' }}
                    render={({ field, fieldState: { error } }) => (
                      <TextField
                        {...field}
                        label="Effective Interest Rate (%)"
                        type="number"
                        inputProps={{ step: '0.01' }}
                        fullWidth
                        required
                        error={!!error}
                        helperText={error?.message}
                      />
                    )}
                  />
                  <Controller
                    name="effective_date"
                    control={control}
                    defaultValue=""
                    rules={{ required: 'Effective date is required' }}
                    render={({ field, fieldState: { error } }) => (
                      <TextField
                        {...field}
                        label="Effective Date"
                        type="date"
                        fullWidth
                        required
                        error={!!error}
                        helperText={error?.message}
                        InputLabelProps={{ shrink: true }}
                      />
                    )}
                  />
                  <Controller
                    name="prime_rate_institution"
                    control={control}
                    defaultValue=""
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Prime Rate Institution"
                        fullWidth
                      />
                    )}
                  />
                  <Controller
                    name="active"
                    control={control}
                    defaultValue={true}
                    render={({ field }) => (
                      <FormControlLabel
                        control={<Switch {...field} checked={field.value} />}
                        label="Active"
                      />
                    )}
                  />
                </>
              )}

              {/* FX Rates Form */}
              {dataType === 'fxRates' && (
                <>
                  <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                    <Controller
                      name="from_currency"
                      control={control}
                      defaultValue=""
                      rules={{ required: 'From currency is required' }}
                      render={({ field, fieldState: { error } }) => (
                        <FormControl fullWidth required error={!!error}>
                          <InputLabel>From Currency</InputLabel>
                          <Select {...field} label="From Currency">
                            {[...new Set(countries.map(c => c.currency_code))].filter(Boolean).sort().map(code => (
                              <MenuItem key={code} value={code}>{code}</MenuItem>
                            ))}
                          </Select>
                          {error && <Typography variant="caption" color="error">{error.message}</Typography>}
                        </FormControl>
                      )}
                    />
                    <Typography variant="h6">/</Typography>
                    <Controller
                      name="to_currency"
                      control={control}
                      defaultValue=""
                      rules={{ required: 'To currency is required' }}
                      render={({ field, fieldState: { error } }) => (
                        <FormControl fullWidth required error={!!error}>
                          <InputLabel>To Currency</InputLabel>
                          <Select {...field} label="To Currency">
                            {[...new Set(countries.map(c => c.currency_code))].filter(Boolean).sort().map(code => (
                              <MenuItem key={code} value={code}>{code}</MenuItem>
                            ))}
                          </Select>
                          {error && <Typography variant="caption" color="error">{error.message}</Typography>}
                        </FormControl>
                      )}
                    />
                  </Box>
                  <Controller
                    name="rate"
                    control={control}
                    defaultValue=""
                    rules={{ required: 'Rate is required' }}
                    render={({ field, fieldState: { error } }) => (
                      <TextField
                        {...field}
                        label="Exchange Rate"
                        type="number"
                        inputProps={{ step: '0.0001' }}
                        fullWidth
                        required
                        error={!!error}
                        helperText={error?.message}
                      />
                    )}
                  />
                  <Controller
                    name="effective_date"
                    control={control}
                    defaultValue=""
                    rules={{ required: 'Effective date is required' }}
                    render={({ field, fieldState: { error } }) => (
                      <TextField
                        {...field}
                        label="Effective Date"
                        type="date"
                        fullWidth
                        required
                        error={!!error}
                        helperText={error?.message}
                        InputLabelProps={{ shrink: true }}
                      />
                    )}
                  />
                  <Controller
                    name="source"
                    control={control}
                    defaultValue=""
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Source"
                        fullWidth
                      />
                    )}
                  />
                  <Controller
                    name="active"
                    control={control}
                    defaultValue={true}
                    render={({ field }) => (
                      <FormControlLabel
                        control={<Switch {...field} checked={field.value} />}
                        label="Active"
                      />
                    )}
                  />
                </>
              )}

              {/* Placement Fees Form */}
              {dataType === 'placementFees' && (
                <>
                  <Controller
                    name="reference"
                    control={control}
                    defaultValue=""
                    rules={{ required: 'Reference is required' }}
                    render={({ field, fieldState: { error } }) => (
                      <TextField
                        {...field}
                        label="Reference"
                        fullWidth
                        required
                        error={!!error}
                        helperText={error?.message}
                      />
                    )}
                  />
                  {/* TYPE IS SEEDED FROM THE TAB, NOT ASKED FOR.
                      This replaced a `freeSolo` Autocomplete whose options were merely derived
                      from existing values, with the helper text "…or type a new one". Because both
                      wizard sections select by EXACT string and the column has NO CHECK
                      constraint, any typo produced a row that was stored, listed here, and
                      selectable in NEITHER section — invisible, with nothing to explain why.
                      REPRODUCED on dev before this change: `type = 'broker fee'` was accepted and
                      appeared in neither dropdown.

                      ⚠ THE CONTROLLER IS DELIBERATELY NOT RENDERED, and must not be re-added as a
                      `disabled` field. react-hook-form (shouldUnregister: false) keeps the seeded
                      value and still submits it — `onSubmit` spreads `{ ...data }` wholesale — but
                      RHF STRIPS DISABLED FIELDS from the payload, which would silently drop `type`
                      and hit the SP's NOT NULL. This is the same trap documented on the
                      interestRates form above for `country_id`. */}
                  <Box sx={{ bgcolor: 'grey.50', border: 1, borderColor: 'divider', borderRadius: 1, p: 1.5 }}>
                    <Typography variant="caption" color="textSecondary" display="block">
                      Type
                    </Typography>
                    <Typography variant="subtitle1" fontWeight="bold">
                      {watch('type') || dialogSeed?.type || '-'}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Set by the tab you are on — a broker fee and a stamp duty are separate charges
                      with their own GL accounts.
                    </Typography>
                  </Box>
                  <Controller
                    name="percentage"
                    control={control}
                    defaultValue=""
                    rules={{ required: 'Percentage is required' }}
                    render={({ field, fieldState: { error } }) => (
                      <TextField
                        {...field}
                        label="Percentage (%)"
                        type="number"
                        inputProps={{ step: '0.0001' }}
                        fullWidth
                        required
                        error={!!error}
                        helperText={error?.message}
                      />
                    )}
                  />
                  <Controller
                    name="dr_expense_account"
                    control={control}
                    defaultValue=""
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Expense Account (DR)"
                        fullWidth
                        helperText="GL debit account — used for journal export of this fee / duty"
                      />
                    )}
                  />
                  <Controller
                    name="cr_control_account"
                    control={control}
                    defaultValue=""
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Control Account (CR)"
                        fullWidth
                        helperText="GL credit account — used for journal export of this fee / duty"
                      />
                    )}
                  />
                  <Controller
                    name="applicable_products"
                    control={control}
                    defaultValue=""
                    render={({ field }) => (
                      <Autocomplete
                        multiple
                        options={investmentTypes.filter(t => t.active !== false)}
                        getOptionLabel={(opt) => typeof opt === 'string' ? opt : (opt.type || '')}
                        value={
                          field.value
                            ? field.value.split(',').map(v => v.trim()).filter(Boolean).map(typeName =>
                                investmentTypes.find(t => t.type === typeName) || { type: typeName }
                              )
                            : []
                        }
                        onChange={(_, newValue) => {
                          field.onChange(newValue.map(v => v.type).join(','));
                        }}
                        isOptionEqualToValue={(option, value) => option.type === value.type}
                        renderInput={(params) => (
                          <TextField {...params} label="Applicable Products" fullWidth />
                        )}
                      />
                    )}
                  />
                  <Controller
                    name="active"
                    control={control}
                    defaultValue={true}
                    render={({ field }) => (
                      <FormControlLabel
                        control={<Switch {...field} checked={field.value} />}
                        label="Active"
                      />
                    )}
                  />
                </>
              )}

              {/* VAT Rates Form */}
              {dataType === 'vatRates' && (
                <>
                  <Controller
                    name="country_id"
                    control={control}
                    defaultValue=""
                    rules={{ required: 'Country is required' }}
                    render={({ field, fieldState: { error } }) => (
                      <FormControl fullWidth required error={!!error}>
                        <InputLabel>Country</InputLabel>
                        <Select {...field} label="Country" value={field.value || ''}>
                          {countries.map((c) => (
                            <MenuItem key={c.id} value={c.id}>
                              {c.country} ({c.currency_code})
                            </MenuItem>
                          ))}
                        </Select>
                        {error && <Typography variant="caption" color="error">{error.message}</Typography>}
                      </FormControl>
                    )}
                  />
                  <Controller
                    name="vat_rate"
                    control={control}
                    defaultValue=""
                    rules={{ required: 'VAT rate is required' }}
                    render={({ field, fieldState: { error } }) => (
                      <TextField
                        {...field}
                        label="VAT Rate (%)"
                        type="number"
                        inputProps={{ step: '0.0001', min: 0 }}
                        fullWidth
                        required
                        error={!!error}
                        helperText={error?.message || 'Standard VAT rate applied to broker fees for this country'}
                      />
                    )}
                  />
                  <Controller
                    name="description"
                    control={control}
                    defaultValue=""
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Description"
                        fullWidth
                        multiline
                        rows={2}
                      />
                    )}
                  />
                  <Controller
                    name="active"
                    control={control}
                    defaultValue={true}
                    render={({ field }) => (
                      <FormControlLabel
                        control={<Switch {...field} checked={field.value} />}
                        label="Active"
                      />
                    )}
                  />
                </>
              )}

              {/* Collateral Types Form */}
              {dataType === 'collateralTypes' && (
                <>
                  <Controller
                    name="type"
                    control={control}
                    defaultValue=""
                    rules={{ required: 'Type is required' }}
                    render={({ field, fieldState: { error } }) => (
                      <TextField
                        {...field}
                        label="Type"
                        fullWidth
                        required
                        error={!!error}
                        helperText={error?.message}
                      />
                    )}
                  />
                  <Controller
                    name="description"
                    control={control}
                    defaultValue=""
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Description"
                        fullWidth
                        multiline
                        rows={3}
                      />
                    )}
                  />
                  <Controller
                    name="active"
                    control={control}
                    defaultValue={true}
                    render={({ field }) => (
                      <FormControlLabel
                        control={<Switch {...field} checked={field.value} />}
                        label="Active"
                      />
                    )}
                  />
                </>
              )}

              {/* Narrations Form */}
              {dataType === 'narrations' && (
                <>
                  <Controller
                    name="narration"
                    control={control}
                    defaultValue=""
                    rules={{ required: 'Narration is required' }}
                    render={({ field, fieldState: { error } }) => (
                      <TextField
                        {...field}
                        label="Narration"
                        fullWidth
                        required
                        error={!!error}
                        helperText={error?.message}
                        multiline
                        rows={2}
                      />
                    )}
                  />
                  <Controller
                    name="classification"
                    control={control}
                    defaultValue=""
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Classification"
                        fullWidth
                      />
                    )}
                  />
                  <Controller
                    name="multiplier"
                    control={control}
                    defaultValue=""
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Multiplier"
                        type="number"
                        inputProps={{ step: '0.01' }}
                        fullWidth
                      />
                    )}
                  />
                  <Controller
                    name="active"
                    control={control}
                    defaultValue={true}
                    render={({ field }) => (
                      <FormControlLabel
                        control={<Switch {...field} checked={field.value} />}
                        label="Active"
                      />
                    )}
                  />
                </>
              )}

              {/* WHT Rules Form */}
              {dataType === 'whtRules' && (
                <>
                  <Controller
                    name="tax_classification"
                    control={control}
                    defaultValue=""
                    rules={{ required: 'Tax classification is required' }}
                    render={({ field, fieldState: { error } }) => (
                      <FormControl fullWidth required error={!!error}>
                        <InputLabel>Tax Classification</InputLabel>
                        <Select {...field} label="Tax Classification">
                          {['Individual', 'Corporate', 'Foreign', 'Trust', 'Exempt'].map((opt) => (
                            <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                          ))}
                        </Select>
                        {error && <Typography variant="caption" color="error">{error.message}</Typography>}
                      </FormControl>
                    )}
                  />
                  <Controller
                    name="country_id"
                    control={control}
                    defaultValue={null}
                    render={({ field }) => (
                      <Autocomplete
                        options={countries}
                        getOptionLabel={(opt) => opt.country ? `${opt.country} (${opt.currency_code})` : ''}
                        value={countries.find((c) => c.id === field.value) || null}
                        onChange={(_, val) => field.onChange(val ? val.id : null)}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Country (leave blank for all countries)"
                          />
                        )}
                      />
                    )}
                  />
                  <Controller
                    name="wht_rate"
                    control={control}
                    defaultValue=""
                    rules={{
                      required: 'WHT rate is required',
                      min: { value: 0, message: 'Rate must be at least 0' },
                      max: { value: 100, message: 'Rate cannot exceed 100' },
                    }}
                    render={({ field, fieldState: { error } }) => (
                      <TextField
                        {...field}
                        label="WHT Rate (%)"
                        type="number"
                        inputProps={{ step: '0.01', min: 0, max: 100 }}
                        fullWidth
                        required
                        error={!!error}
                        helperText={error?.message}
                      />
                    )}
                  />
                  <Controller
                    name="description"
                    control={control}
                    defaultValue=""
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Description"
                        fullWidth
                        multiline
                        rows={2}
                      />
                    )}
                  />
                  <Controller
                    name="effective_from"
                    control={control}
                    defaultValue=""
                    rules={{ required: 'Effective from date is required' }}
                    render={({ field, fieldState: { error } }) => (
                      <TextField
                        {...field}
                        label="Effective From"
                        type="date"
                        fullWidth
                        required
                        error={!!error}
                        helperText={error?.message}
                        InputLabelProps={{ shrink: true }}
                      />
                    )}
                  />
                  <Controller
                    name="effective_to"
                    control={control}
                    defaultValue=""
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Effective To (optional)"
                        type="date"
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                      />
                    )}
                  />
                  <Controller
                    name="active"
                    control={control}
                    defaultValue={true}
                    render={({ field }) => (
                      <FormControlLabel
                        control={<Switch {...field} checked={field.value} />}
                        label="Active"
                      />
                    )}
                  />
                </>
              )}

              {/* Commission Rates Form */}
              {dataType === 'commissionRates' && (
                <>
                  <Controller
                    name="broker_id"
                    control={control}
                    defaultValue={null}
                    render={({ field: { onChange, value, ...field } }) => (
                      <Autocomplete
                        {...field}
                        options={brokersList}
                        getOptionLabel={(option) => option.name || ''}
                        value={value ? brokersList.find((b) => b.id === value) || null : null}
                        onChange={(_, data) => onChange(data?.id ?? null)}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Broker (optional)"
                            fullWidth
                            helperText="Leave blank to apply to all brokers"
                          />
                        )}
                        isOptionEqualToValue={(option, val) => option.id === val?.id}
                      />
                    )}
                  />
                  <Controller
                    name="investor_id"
                    control={control}
                    defaultValue={null}
                    render={({ field: { onChange, value, ...field } }) => (
                      <Autocomplete
                        {...field}
                        options={investorsList}
                        getOptionLabel={(option) => option.name || ''}
                        value={value ? investorsList.find((i) => i.id === value) || null : null}
                        onChange={(_, data) => onChange(data?.id ?? null)}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Investor (optional)"
                            fullWidth
                            helperText="Leave blank to apply to all investors"
                          />
                        )}
                        isOptionEqualToValue={(option, val) => option.id === val?.id}
                      />
                    )}
                  />
                  <Controller
                    name="rate_type"
                    control={control}
                    defaultValue=""
                    rules={{ required: 'Rate type is required' }}
                    render={({ field, fieldState: { error } }) => (
                      <FormControl fullWidth required error={!!error}>
                        <InputLabel>Rate Type</InputLabel>
                        <Select {...field} label="Rate Type">
                          <MenuItem value="spread">Spread</MenuItem>
                          <MenuItem value="flat_fee">Flat Fee</MenuItem>
                          <MenuItem value="tiered">Tiered</MenuItem>
                        </Select>
                        {error && <Typography variant="caption" color="error">{error.message}</Typography>}
                      </FormControl>
                    )}
                  />
                  <Controller
                    name="rate"
                    control={control}
                    defaultValue=""
                    rules={{ required: 'Rate is required' }}
                    render={({ field, fieldState: { error } }) => (
                      <TextField
                        {...field}
                        label="Rate (%)"
                        type="number"
                        inputProps={{ step: '0.01' }}
                        fullWidth
                        required
                        error={!!error}
                        helperText={error?.message}
                      />
                    )}
                  />
                  <Controller
                    name="min_amount"
                    control={control}
                    defaultValue=""
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Min Amount (optional)"
                        type="number"
                        inputProps={{ step: '0.01' }}
                        fullWidth
                        onChange={(e) => field.onChange(e.target.value === '' ? null : e.target.value)}
                        value={field.value ?? ''}
                      />
                    )}
                  />
                  <Controller
                    name="max_amount"
                    control={control}
                    defaultValue=""
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Max Amount (optional)"
                        type="number"
                        inputProps={{ step: '0.01' }}
                        fullWidth
                        onChange={(e) => field.onChange(e.target.value === '' ? null : e.target.value)}
                        value={field.value ?? ''}
                      />
                    )}
                  />
                  <Controller
                    name="effective_from"
                    control={control}
                    defaultValue=""
                    rules={{ required: 'Effective from date is required' }}
                    render={({ field, fieldState: { error } }) => (
                      <TextField
                        {...field}
                        label="Effective From"
                        type="date"
                        fullWidth
                        required
                        error={!!error}
                        helperText={error?.message}
                        InputLabelProps={{ shrink: true }}
                      />
                    )}
                  />
                  <Controller
                    name="active"
                    control={control}
                    defaultValue={true}
                    render={({ field }) => (
                      <FormControlLabel
                        control={<Switch {...field} checked={field.value} />}
                        label="Active"
                      />
                    )}
                  />
                </>
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button type="submit" variant="contained">
              {editingItem ? 'Update' : 'Create'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Batch historic interest-rate entry. Sits outside the interest_rates feature block, so it
          self-gates on activeFeature as well as its own open state. */}
      <Dialog
        open={activeFeature === 'interest_rates' && !!batchDialog}
        onClose={handleCloseBatchDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Add Historic Rates{batchDialog?.interest_rate_type ? ` — ${batchDialog.interest_rate_type}` : ''}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            {batchDialog?.country_label && (
              <Box sx={{ bgcolor: 'grey.50', border: 1, borderColor: 'divider', borderRadius: 1, p: 1.5 }}>
                <Typography variant="caption" color="textSecondary" display="block">
                  Adding to rate type
                </Typography>
                <Typography variant="subtitle1" fontWeight="bold">
                  {batchDialog.interest_rate_type}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {batchDialog.country_label}
                </Typography>
              </Box>
            )}

            <Typography variant="body2" color="text.secondary">
              Add one line per historic rate. All lines are saved together — if any line is rejected,
              nothing is saved. Institution and rate settings are taken from this rate type.
            </Typography>

            {batchError && (
              <Alert severity="error" onClose={() => setBatchError(null)}>
                {batchError}
              </Alert>
            )}

            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={handleAddBatchRow}>
                Add Row
              </Button>
            </Box>

            {batchRows.length === 0 ? (
              <Paper sx={{ p: 3, textAlign: 'center', bgcolor: 'grey.50' }}>
                <Typography color="text.secondary">
                  No rows. Click "Add Row" to start.
                </Typography>
              </Paper>
            ) : (
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'grey.50' }}>
                      <TableCell width="45%">Effective Date</TableCell>
                      <TableCell width="35%">Rate %</TableCell>
                      <TableCell width="20%" align="center">Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {batchRows.map((row, index) => {
                      const rowError = batchValidation.rowErrors[index];
                      const serverFailed = batchFailedRow === index;
                      return (
                        <React.Fragment key={row.key}>
                          <TableRow sx={serverFailed ? { bgcolor: 'error.50' } : undefined}>
                            <TableCell>
                              <TextField
                                type="date"
                                value={row.effective_date}
                                onChange={(e) => handleUpdateBatchRow(index, 'effective_date', e.target.value)}
                                size="small"
                                fullWidth
                                error={!!rowError || serverFailed}
                                InputLabelProps={{ shrink: true }}
                              />
                            </TableCell>
                            <TableCell>
                              <TextField
                                type="number"
                                value={row.effective_interest_rate}
                                onChange={(e) => handleUpdateBatchRow(index, 'effective_interest_rate', e.target.value)}
                                size="small"
                                fullWidth
                                placeholder="0.00"
                                inputProps={{ step: '0.01', min: 0 }}
                                error={!!rowError || serverFailed}
                              />
                            </TableCell>
                            <TableCell align="center">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleRemoveBatchRow(index)}
                                title="Remove row"
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                          {(rowError || serverFailed) && (
                            <TableRow>
                              <TableCell colSpan={3} sx={{ py: 0.5, border: 0 }}>
                                <Typography variant="caption" color="error">
                                  {rowError || 'This row was rejected — nothing was saved.'}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            <Typography variant="caption" color="text.secondary">
              {batchValidation.sectionError
                ? batchValidation.sectionError
                : `${batchValidation.filledCount} rate${batchValidation.filledCount === 1 ? '' : 's'} ready to save`}
              . Blank rows are ignored. Dates further back than the tenant's configured backdate limit
              are rejected — raise "Max Backdate Days (Rate Changes)" in Tenant Settings to load older
              history. Only the most recent rate in a group stays editable afterwards.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseBatchDialog} disabled={batchSubmitting}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSubmitBatch}
            disabled={!batchValidation.canSubmit || batchSubmitting}
          >
            {batchSubmitting ? 'Saving…' : 'Save All'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Impact Assessment Prompt Dialog */}
      <Dialog
        open={impactDialog.open}
        onClose={() => setImpactDialog({ ...impactDialog, open: false })}
      >
        <DialogTitle>Run Impact Assessment?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This interest rate change may impact variable-rate contracts linked to{' '}
            <strong>{impactDialog.rateType}</strong>.
            <br /><br />
            Would you like to run an impact assessment to see which contracts will be affected?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImpactDialog({ ...impactDialog, open: false })}>
            No, Thanks
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              const params = new URLSearchParams({
                type: impactDialog.rateType,
                country: impactDialog.countryId,
                rate: impactDialog.newRate,
                date: impactDialog.effectiveDate,
                benchmark_id: impactDialog.benchmarkRateId,
              });
              navigate(`/rate-changes/impact?${params.toString()}`);
            }}
          >
            Run Impact Assessment
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog {...confirm.dialogProps} />
    </Box>
  );
}

export default ReferenceDataManagement;
